import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type FormEvent,
} from 'react';
import { FormErrorSummary } from '@/components/catalog/FormShell';
import { without } from '@/components/catalog/form-utils';
import { DealLineItemsTable } from '@/components/deals/DealLineItemsTable';
import { PickerField } from '@/components/deals/PickerField';
import { LostReasonFields } from '@/components/deals/DealLostReasonDialog';
/*
 * The Next task note, arranged for a deal. ⚠️ Moved OUT of this file on
 * 2026-08-31, when the inline editor on the deal record started writing the same
 * two notes: what would drift between two copies is the BODY landing on a
 * company's timeline and the rule deciding whether a note is written at all, and
 * neither screen can see the other disagreeing with it. It still goes down to
 * `writeDraftNote`, so the refusal sentences — including the `/notes` 403, which
 * is real here — are still worded once.
 */
import {
  type NextTaskNoteFailure,
  nextTaskNoteText,
  writeNextTaskNotes,
} from '@/components/deals/next-task-note';
import {
  type NextTaskReminderFailure,
  createNextTaskReminder,
  shouldRaiseReminder,
} from '@/components/deals/next-task-reminder';
import { ContactPickerField } from '@/components/contacts/ContactPickerField';
import { ApiError, api } from '@/lib/api';
/*
 * ⚠️ **`PlannedProductLine` lives in `@/lib/api`, deliberately, and is not
 * restated here.** `plannedProductLines()` already decides what a planned row
 * carries and whether a planned decimal quantity can be a line quantity; a
 * look-alike interface in a form file would be a SECOND home for those
 * decisions, and the two would agree the day they were written and drift on the
 * first correction. `QuoteFormModal` imports the same type from the same place
 * for the same reason — so all three document forms seed from one shape.
 */
import type { PlannedProductLine } from '@/lib/api';
import {
  CONTACT_NOT_ON_COMPANY,
  contactScopeIsAuthoritative,
  primaryContactSeed,
} from '@/lib/contact-scope';
import { currentCycle, defaultDocumentName } from '@/lib/document-naming';
import { denialReason } from '@/lib/permissions';
import { useAuth } from '@/lib/use-auth';
import {
  CURRENCY_PATTERN,
  DEAL_MAX_LENGTHS as MAX_LENGTHS,
  validateDealAmount,
  validateDealClosingDate,
  validateDealCurrency,
  validateDealName,
} from '@/lib/document-validation';
import { useCycleOptions } from '@/lib/use-cycle-options';
import { useDealPickers } from '@/lib/use-deal-pickers';
/*
 * The currency master lives in `use-invoice-options` because invoices needed it
 * first; it is not invoice-specific — `DealsPage` and `QuoteFormModal` both
 * import it for the same reason. Imported rather than re-derived so this picker
 * offers the same set, the same order and, above all, the same VALUE as every
 * other currency control in the app: the three-letter CHAR(3) CODE, never a
 * master row id. `d_deals.currency` carries no foreign key into `f_currencies`,
 * so an id sent here would be stored as a number or coerced into nonsense.
 */
import { currencyOptions, useCurrencyOptions } from '@/lib/use-invoice-options';
import { useUserOptions } from '@/lib/use-user-options';
import { USER_GROUP_SLUGS, narrowingNote, outsideGroupNote } from '@/lib/user-groups';
import type {
  CompanyApprovalStatus,
  Deal,
  DealCreateInput,
  DealProductCreateInput,
  DealStage,
  DealUpdateInput,
  ProjectCity,
  UserSummary,
} from '@/lib/types';
import { Button, Combobox, Field, Input, Modal } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import type { ComboboxOption } from '@/components/ui';

/**
 * What a caller may seed a NEW deal with.
 *
 * 🔴 **OWNER'S INSTRUCTION, 2026-09-13**: the "+" on a company row opens the
 * create form with line items already seeded from that company's projects of
 * interest, for Quote, Invoice **and Deal**. The first two already accepted
 * `prefill.lines`; this interface is the deal's half of that.
 *
 * ⚠️ **This REVERSES a decision recorded in `DealLineItemsTable.tsx` on
 * 2026-09-11** — that the deal grid took no seed. That comment is marked
 * superseded there rather than deleted: the reasoning it carries is still the
 * reason the seam is shaped this way, only the conclusion moved.
 *
 * ⛔ **`lines` IS THE ONLY KEY, AND STARTING SMALL IS THE DECISION, not an
 * unfinished job.** `company`, `contact` and `currency` all look like obvious
 * next members and none of them is one edit:
 *
 *   - each would have to be audited against `contactTouched`, `currencyTouched`
 *     and `stageTouched` — the flags that stop a seed re-applying itself over
 *     something a person has since changed, which is how a seeded field starts
 *     fighting the user;
 *   - a seeded company interacts with `openedCompanyId` and the once-per-
 *     selection `getCompany` read behind `companyLead`, which is what supplies
 *     the naming, the primary contact and the approval status;
 *   - 🔴 **and a prefilled company MUST NOT bypass `newDealCompanyError` — with
 *     ONE named exemption, and it is NOT this one.** ⚠️ *SUPERSEDED 2026-09-20,
 *     the ABSOLUTE form: "🔴 **and a prefilled company MUST NOT bypass
 *     `newDealCompanyError`.** A deal may only be raised against an APPROVED
 *     company, that check is client-side, and — see the paragraph at that
 *     function — it is **the only rule deals have**: `deals.service.ts` never
 *     calls the approval guard, so `POST /deals` would answer `201` and nothing
 *     would fail."* **Every clause of that is still true of the MECHANISM**, and
 *     what moved is that the owner carved out exactly one PATH: the B2B-proposal
 *     convert flow opts in through
 *     {@link DealFormModalProps.allowCompanyAwaitingApproval}, because it creates
 *     the company, raises it for approval and raises the deal in one sitting, so
 *     the company is `pending` by construction and the old rule refused the very
 *     case the flow exists for.
 *     ⛔ **THE CARVE-OUT IS THAT PROP AND NOTHING ELSE. A PREFILL STILL BYPASSES
 *     NOTHING**, which is why this bullet is kept rather than struck: a prefilled
 *     company is named here as precisely what must NOT be inferred from, so a
 *     caller that seeds lines and does not pass that prop meets the refusal
 *     exactly as it stood. ⚠️ **And the exemption is `pending` ALONE** —
 *     `rejected` is refused on every path, opt-in included.
 *
 * **Add one of them when it is asked for, with that audit done.** Widening this
 * type is cheap; widening it without the audit is silent.
 */
export interface DealFormPrefill {
  /**
   * The planned basket the new deal OPENS WITH — rows on the draft line grid,
   * visible and adjustable, before anything is created.
   *
   * ⚠️ **PREFILLING IS NOT RAISING.** These are a seed, not a commitment: the
   * person still sees the modal, still edits the rows, and still presses Save.
   * Nothing on this path creates a deal without that.
   *
   * ⚠️ **It does not land in `values`, and could not.** A deal line is
   * `POST /deals/{id}/products` and **cannot exist before the deal does**, so
   * this seeds the DRAFT GRID and is written by the existing two-phase create —
   * deal first, then each line, then a report naming any that were refused.
   * **Nothing new happens on save**, which is what keeps the non-atomic create
   * path untouched.
   *
   * ⚠️ **A seeded row the grid cannot state — an unstatable quantity — still
   * BLOCKS SAVE**, through `blockedDraftRows`, exactly as a hand-typed one
   * does. A seed is not a licence to send a row the grid would refuse.
   *
   * ⚠️ **It is part of the REMOUNT KEY** — see {@link createKeyOf}. Two seeded
   * raises for one company agree on every other term, so without the lines the
   * second open would carry the first seed's basket.
   *
   * 🔴 **AND IT IS THE ONLY WAY A PROJECT CAN TRAVEL INTO A NEW DEAL.**
   * `PlannedProductLine.project` seeds the LINE's project picker.
   * ⛔ **There is deliberately NO `project` KEY ON THIS INTERFACE AND THERE
   * CANNOT EVER BE ONE.** The header project picker was removed from all three
   * document forms on the owner's instruction; `projectId` is absent from
   * `FormValues`, `ID_KEYS` and `buildPayload` **structurally**, so no payload
   * this form can build expresses one — and an explicit `null` on a header
   * `projectId` CLEARS a live column with a `200`, silently, on the first
   * unrelated edit. A project belongs to a LINE, which is the model
   * `f_deal_products.project_id` actually has.
   */
  lines?: readonly PlannedProductLine[];
}

export interface DealFormModalProps {
  open: boolean;
  onClose: () => void;
  /** Omitted = create mode. Provided = edit mode. */
  deal?: Deal;
  /**
   * Pre-selects and locks the company when the form is opened from a company
   * record — the "+ New deal" route off the companies list, which arrives here
   * as `?companyId=N&new=1`. The name travels with the id so the read-only
   * field can show it without a lookup round-trip of its own.
   */
  lockedCompany?: { id: number; name: string };
  /**
   * What a CREATE opens with. Ignored in edit mode — a stored deal's own rows
   * are the record's, and the edit-branch grid owns its own writes.
   *
   * See {@link DealFormPrefill} for why `lines` is the only member.
   */
  prefill?: DealFormPrefill;
  /**
   * 🔴 **Lets a CREATE name a company whose approval is still PENDING.**
   * Defaults to `false`, which is every other caller and the rule unchanged.
   *
   * ## ⛔ WHO MAY PASS IT: the B2B-proposal convert flow, and nothing else.
   *
   * **Owner's decision, 2026-09-20.** Converting an enquiry creates the company,
   * raises it for approval and raises the deal **in one sitting** — that is the
   * whole feature. A company created seconds ago by that very flow is `pending`
   * **by construction**, so {@link newDealCompanyError} was refusing exactly the
   * case the flow exists for. The exemption is the narrowest thing that unblocks
   * it: one path, opted into once, at one call site.
   *
   * ⛔ **IT IS OPT-IN AND EXPLICIT, AND IT IS INFERRED FROM NOTHING.** Not from
   * {@link DealFormModalProps.lockedCompany}, not from a `prefill` being present,
   * not from the two together. {@link DealFormPrefill} names a prefilled company
   * as precisely what must NOT bypass the refusal, so inferring this from the
   * seed would be the reverse of what was decided — and a lock arrives from the
   * ordinary "+ New deal" route off the companies list, where the refusal is
   * exactly what should fire. **A caller that wants this says so.**
   *
   * ⛔ **DO NOT PASS IT FROM ANYTHING ELSE, AND DO NOT SPREAD IT IN.** It is not
   * a convenience for a form that met an inconvenient refusal, and it is not the
   * "deals are unguarded anyway" argument wearing a prop: `deals.service.ts`
   * never calls the approval guard, so **this flag is the only thing standing
   * between an unapproved company and a `201`** — see the paragraph at
   * {@link newDealCompanyError} for why that check has no server behind it.
   *
   * ⚠️ **`pending` ALONE. A REJECTED company is still refused, with the flag
   * set.** The two send a reader to opposite places — one waits for an approver,
   * the other is over — and nothing about converting an enquiry makes a rejection
   * stale. The refusal function tests the status rather than skipping the check.
   *
   * ## 🔴 THE COST, WHICH THE OWNER ACCEPTED KNOWINGLY
   *
   * **If the approver REJECTS the company, there is a live deal naming a rejected
   * company and NOTHING TODAY CLEANS THAT UP.** No sweep, no cascade, no notice:
   * the deal simply goes on naming it. ⚠️ **And a rejected company's NAME is
   * FREED for reuse**, so somebody can recreate that school as a different record
   * — leaving the deal pointing at a row that is not the account anybody thinks
   * it is, with nothing on screen saying so. **That is the reason this is a
   * narrow exemption rather than a relaxation of the rule**, and it is the reason
   * to refuse any request to widen it to `rejected` or to another caller.
   *
   * ⚠️ **IT REACHES THE SUBMIT-TIME REFUSAL AND NOT THE PICKER.** The company
   * dropdown still offers approved companies only on create (`withholdUnapproved`
   * — see `useLookupSearch`), and widening that would offer every unapproved
   * company in the estate, which is a far larger decision nobody has taken. The
   * convert flow does not meet that, because it arrives with a
   * {@link DealFormModalProps.lockedCompany} and **a locked company renders no
   * picker at all**. A caller that set this flag with no lock would find its
   * pending company absent from the dropdown; that is a limit of the exemption,
   * not a bug to route around.
   */
  allowCompanyAwaitingApproval?: boolean;
  onSaved: (deal: Deal) => void;
}

/*
 * ⚠️ `MAX_LENGTHS` and the field-rule validators are imported from
 * `@/lib/document-validation`, shared with `InlineDealFields`, which edits
 * the same columns one at a time. Do not re-declare any of them here.
 */

/**
 * What the company's account lead is, as far as this form can honestly say.
 *
 * `known` is false until it has been READ — no company chosen, fetch in
 * flight, or failed. An unread answer and "no account lead" are different
 * facts; reporting the first as the second is the mistake this exists to stop.
 */
interface CompanyLead {
  known: boolean;
  lead: UserSummary | null;
  /**
   * The chosen company's naming fields, for the default deal name.
   *
   * `null` means UNREAD — no company, fetch in flight or failed, or the
   * edit-mode seed short-circuit (which carries only the lead, never used
   * for naming since a stored name is the record's own). Read once per
   * selection, alongside the account lead, to spend one request instead of two.
   *
   * ⚠️ Not derived from `lockedCompany` or the picker option — both carry
   * `{id, name}` only, and `shortName` cannot be guessed from a name.
   */
  naming: { name: string; shortName: string | null } | null;
  /**
   * The chosen company's designated primary contact, for the Contact seed.
   *
   * Read from the SAME `getCompany` response as the lead and the naming, so one
   * request answers all three and no two of them can describe different
   * companies.
   *
   * ⚠️ `null` here folds together UNREAD and "this company designates nobody",
   * and that conflation is safe HERE and nowhere else: both resolve to "seed
   * nothing, leave the picker empty", which is exactly what the caller does with
   * it. It must never be rendered as a statement that the account has no primary
   * — a company that could not be read has not said so.
   */
  primaryContact: { id: number; name: string } | null;
  /**
   * The chosen company's default currency CODE, for the Currency seed.
   *
   * Read from the SAME `getCompany` response as the lead, the naming and the
   * primary contact, so one request answers all four and no two of them can
   * describe different companies.
   *
   * ⚠️ The CODE, never `currency.id`. `Company.currency.id` is nullable
   * precisely because the legal set is Xero's rather than our master's, so a
   * company can perfectly well default to a code `/currencies` has never
   * carried — and `d_deals.currency` stores the code anyway.
   *
   * ⚠️ `null` folds together UNREAD, "this company names no default" and "the
   * code it names is not three capital letters". All three resolve to *seed
   * nothing*, which is the only thing the caller does with it. It must never be
   * rendered as a statement that the account has no currency.
   */
  currency: string | null;
  /**
   * Whether the chosen company has been APPROVED — the fact the create-time
   * refusal turns on. See {@link newDealCompanyError}.
   *
   * Fifth rider on the same `getCompany` read as the lead, the naming, the
   * primary contact and the currency, so one request answers all five and no
   * two of them can describe different companies. It is read here rather than
   * off the picker option because a picker option is not always there to read:
   * a company arriving through `lockedCompany` renders no picker at all, and
   * that route is exactly how somebody reaches this form from a company they
   * have just raised.
   *
   * ⚠️ **`null` is UNREAD, and it must NEVER be read as "not approved".** No
   * company chosen, fetch in flight, fetch FAILED, or the edit-mode seed
   * short-circuit (which carries only the lead). A request that did not come
   * back is not evidence about a company's status, so the refusal below stands
   * down on `null` and lets the save proceed — the same rule this codebase
   * applies to an `'unknown'` inclusions signal and to an unclassified Xero tax
   * rate. Withholding an option is recoverable; refusing a save on a fact
   * nobody read is a false statement the person cannot argue with.
   */
  approvalStatus: CompanyApprovalStatus | null;
}

/**
 * This app's own refusal for the company on a NEW deal — `null` when there is
 * nothing to refuse.
 *
 * Owner's rule, 2026-08-31: **a new deal requires a company, and that company
 * must be approved.** Both halves are create-time only; see the call site in
 * `validate`.
 *
 * ## ⚠️ ON DEALS THIS REALLY IS A CLIENT RULE — verified 2026-08-31, and the
 * ## quote and invoice forms NO LONGER have one. Do not harmonise the three.
 *
 * The billing gate is a SERVER guard: `assertCompanyApproved(companyId, action)`
 * in `emerge-backend-app/src/modules/companies/company-approval.guard.ts`, added
 * by **migration 099**, throwing a **409** under one of three codes. It is
 * called from `createQuote`, `createInvoice`, `quote-convert.ts` and both
 * conversions in `deal-convert.ts` — so `QuoteFormModal` and `InvoiceFormModal`
 * have had their invented sentences DELETED and surface the server's instead,
 * through `billingGateRefusalOf` in `@/lib/company-approval`.
 *
 * ⚠️ **`deals.service.ts` never calls the guard, and that is deliberate rather
 * than an omission.** The guard's own call-site list says so in terms: *"a deal
 * is a forecast, not a demand for money, and refusing one would stop somebody
 * recording a live opportunity while an approval is in flight."* The two deal
 * conversions are where the refusal lands instead — which is where the money is.
 *
 * ⚠️ **So the sentences below are the ONLY rule a deal has, and deleting them to
 * match the other two forms removes it entirely** — silently, because `POST
 * /deals` would answer `201` and nothing would fail. That is the whole reason
 * this paragraph is here.
 *
 * None of it may read as though the API refused the write, none of it carries an
 * `error.code`, and none of it is access control: it decides what this form will
 * send, not what anybody is allowed to do.
 *
 * ⚠️ Two sentences rather than one, because `pending` and `rejected` send the
 * reader to different places — one waits for an approver, the other is over.
 * "Not approved" alone would be true of both and actionable for neither. **That
 * distinction is what made the 2026-09-20 exemption below expressible at all**:
 * one path may proceed on a company still awaiting an approver, and no path may
 * proceed on one an approver has turned down.
 *
 * ## 🔴 ONE PATH IS EXEMPT ON `pending`, SINCE 2026-09-20
 *
 * `allowAwaitingApproval` — owner's decision, and see
 * {@link DealFormModalProps.allowCompanyAwaitingApproval}, where the whole
 * argument and the accepted cost live. In one line: the B2B-proposal convert
 * flow creates the company, raises it for approval and raises the deal in one
 * sitting, so the company is `pending` by construction and this function was
 * refusing the case that flow exists for.
 *
 * ⛔ **THE PARAMETER IS REQUIRED, NOT DEFAULTED, AND THAT IS THE GUARD.** A
 * default of `false` would be right at every call site that exists and would let
 * the next one omit it without saying anything; required, `tsc` makes every call
 * site state its answer out loud. Same reasoning this codebase records for the
 * required `lineProjects` mapper argument.
 *
 * ⛔ **IT IS A NARROWING OF ONE BRANCH, NEVER A SKIP OF THE CHECK.** The status
 * is still read, `rejected` is still refused, and the missing-company sentence
 * still fires — so a caller passing `true` has widened exactly one answer and
 * nothing else. **Do not "simplify" this to an early return.**
 */
function newDealCompanyError(
  companyId: string,
  approvalStatus: CompanyApprovalStatus | null,
  allowAwaitingApproval: boolean,
): string | null {
  if (companyId.trim() === '') {
    return 'Choose a company — a new deal has to be raised against one.';
  }
  /*
   * ⚠️ UNREAD, so nothing is refused. See `CompanyLead.approvalStatus`: this is
   * the branch a failed `getCompany` lands in, and refusing here would turn a
   * request that never came back into an accusation against a company that may
   * be perfectly approved.
   */
  if (approvalStatus === null) return null;
  if (approvalStatus === 'approved') return null;
  /*
   * 🔴 The 2026-09-20 exemption, and it is `pending` ALONE — tested on the
   * status rather than short-circuiting the function, so `rejected` falls
   * through to its own sentence below with the flag set. A caller that opted in
   * has widened one answer; it has not turned the rule off.
   */
  if (approvalStatus === 'pending' && allowAwaitingApproval) return null;
  return approvalStatus === 'rejected'
    ? 'That company was rejected, so a deal cannot be raised against it.'
    : 'That company is still awaiting approval, so a deal cannot be raised against it yet.';
}

/**
 * The Combobox footnote reporting companies withheld from the list because they
 * are not approved — `undefined` when none were.
 *
 * ⚠️ It exists because a company somebody can see on `/companies` and cannot
 * find in this dropdown reads as a broken fetch, and they go hunting. A
 * narrowed list that says nothing is the silent failure this codebase already
 * records against retired picker options.
 *
 * ⚠️ It says the company is not APPROVED, never that it does not exist. The
 * wording covers `pending` and `rejected` alike without claiming which, because
 * the list cannot say which without naming the company — and the count is the
 * part that stops the hunt.
 */
function withheldCompanyNote(withheld: number): string | undefined {
  if (withheld <= 0) return undefined;
  return withheld === 1
    ? '1 company is not listed — a deal can only be raised against an approved company.'
    : `${withheld} companies are not listed — a deal can only be raised against an approved company.`;
}

/**
 * What the account-lead picker's EMPTY row says.
 *
 * ⚠️ Never "None" and never "Unassigned" while a company is chosen.
 * `accountLeadId: null` does not mean nobody leads the deal — it CLEARS THE
 * OVERRIDE and returns the deal to following its company. So the row names
 * the outcome, naming the person where the company's own choice is known,
 * which turns "clear the field" into a legible revert.
 *
 * The one case where clearing really does leave nobody is a deal with NO
 * company — an existing deal already holding an override with nothing to
 * fall back to.
 */
function followOptionLabel(hasCompany: boolean, company: CompanyLead): string {
  if (!hasCompany) return 'No account lead — there is no company to follow';
  if (!company.known) return 'Follow the company';
  return company.lead === null
    ? 'Follow the company (it names no account lead)'
    : `Follow the company (${company.lead.name})`;
}

/**
 * A line that did not attach, named by its POSITION in the grid as
 * submitted, never by product name — this form holds no product directory
 * (the grid owns the catalogue), and position is what the person can match
 * against the rows they typed. Same shape as `QuoteFormModal`'s
 * `DraftLineOutcome`.
 *
 * A refused line HAS no id, so there is nothing else to identify it by.
 */
interface LineFailure {
  /** 1-based, in the order the lines were sent. */
  position: number;
  message: string;
}

/**
 * What went wrong on ONE line, in words worth showing.
 *
 * ⚠️ Not `error.message` — on a 422 that's only "Validation failed"; the
 * useful sentence lives in `details`, which is where every deal-line refusal
 * (the seven project/city sentences, strict-schema rejections) actually is.
 *
 * Everything is form-level here by construction: this form renders no
 * control for any line key once the grid is off screen, so nothing binds a
 * path — hence the text is spelled out in full. The 409 (product already on
 * the deal under that project) carries its text in `message`, kept as the
 * fallback.
 */
function lineFailureMessage(caught: unknown): string {
  if (!(caught instanceof ApiError)) {
    return 'The server could not be reached, so this line was not sent.';
  }
  const detail = [...Object.values(caught.fieldErrors), ...caught.formMessages];
  return detail.length > 0 ? detail.join(' ') : caught.message;
}

/**
 * The header project's cities on a CREATE — empty, permanently: a deal
 * raised here carries no header project, so `[]` is the true answer, not a
 * placeholder. Hoisted to module scope so identity is stable across renders.
 */
const NO_HEADER_CITIES: ProjectCity[] = [];

/*
 * What a refused line run says on the form, in the two shapes it can happen in.
 *
 * ⚠️ **Neither may be worded as a failed SAVE.** On the second the PATCH has
 * landed, and somebody who reads "failed" edits the deal again over figures
 * that are already stored. Both point at the grid, because the per-row reason —
 * the server's own sentence — is on the row and nowhere else. Worded to match
 * `QuoteFormModal`, so the three forms cannot say this three different ways.
 */
const LINES_NOT_SAVED = 'The line items were not saved — see the report on the rows above.';
const DEAL_SAVED_LINES_NOT =
  'The deal’s details were saved. Its line items were not — see the report on the rows above.';

/** Every value is a string, so the edit baseline can be diffed key by key. */
interface FormValues {
  dealName: string;
  contactId: string;
  companyId: string;
  stageId: string;
  /**
   * The deal's OWN account lead — the OVERRIDE, never the resolved value.
   * `''` is "follow the company" (`accountLeadId: null` on write). A deal
   * merely following its company opens EMPTY even though `deal.accountLead`
   * names somebody — seeding the resolved value would show a company's
   * choice as though this deal made it, and the first save would freeze
   * that person on as an override nobody asked for.
   */
  accountLeadId: string;
  amount: string;
  currency: string;
  closingDate: string;
  nextStep: string;
}

/**
 * Keys that carry a foreign key id.
 *
 * ⚠️ `dealTypeId` and `leadSourceId` are DELIBERATELY ABSENT, and their absence
 * is load-bearing in exactly the way `projectId`'s and `ownerId`'s are (see
 * `buildPayload`). The owner's decision is that neither field is touched from
 * this modal at all — so the controls went, and the keys had to go WITH them.
 * Leaving a key here with no control would emit `dealTypeId: null` on the first
 * EDIT that changed anything else, and an explicit `null` CLEARS the column
 * where an omitted key leaves it alone: every deal's stored type and lead source
 * silently wiped by an unrelated save. A key that is not a `FormValues` member
 * can never reach `changedKeys`, and one that is not here can never reach the
 * POST loop — which is what makes the removal structural rather than a promise.
 *
 * Do not add either back without adding a control that says what is written.
 * `InlineDealFields` on the deal detail page still edits both; that is a
 * separate surface and a separate decision.
 */
const ID_KEYS = ['contactId', 'companyId', 'stageId', 'accountLeadId'] as const;

/**
 * Keys that are plain nullable text.
 *
 * ⚠️ `description` and `xeroQuoteId` are DELIBERATELY ABSENT, on the owner's
 * decision (2026-08-31) to take both controls off every deal surface. Their
 * absence is structural in the same way `dealTypeId`'s and `projectId`'s are:
 * a key left here with no control emits `description: null` on the first EDIT
 * that changes anything else, and an explicit `null` CLEARS the column where
 * an omitted key leaves it alone — so a live deal's description would be
 * destroyed by somebody renaming it, with a 200 and nothing to restore from.
 *
 * ⚠️ **`description` HOLDS REAL DATA on live deals and is still READ** —
 * `Deal.description` is untouched on the response type and the column is
 * untouched in the database. Removing the control HIDES it; it deletes
 * nothing. Restoring it means a `FormValues` member, an entry here and a
 * control, plus the same three in `InlineDealFields`.
 *
 * `xeroQuoteId` is the safer half: it is writable and populated by NOTHING —
 * a hand-typed third copy from the Zoho era, while the authoritative Xero
 * reference lives on `f_xero_links` and is read through `linkOf`.
 */
const TEXT_KEYS = ['nextStep'] as const;

function toFormValues(deal?: Deal): FormValues {
  return {
    dealName: deal?.dealName ?? '',
    contactId: deal?.contact ? String(deal.contact.id) : '',
    companyId: deal?.company ? String(deal.company.id) : '',
    stageId: deal?.stage ? String(deal.stage.id) : '',
    // The OVERRIDE only. `accountLeadIsOverride: false` means the deal stores
    // nothing and follows its company, which is an empty control here.
    accountLeadId:
      deal?.accountLeadIsOverride && deal.accountLead ? String(deal.accountLead.id) : '',
    amount: deal?.amount ?? '',
    currency: deal?.currency ?? '',
    closingDate: deal?.closingDate ?? '',
    nextStep: deal?.nextStep ?? '',
  };
}

/**
 * The `path` values a 422 may name that this form can actually MARK.
 *
 * ⚠️ These are the keys the form SENDS — derived from `toFormValues()` so
 * the list cannot drift from the controls. Deliberately no `projectId` and
 * no `cityIds`: neither is ever sent, so neither can come back on a `path`.
 *
 * Anything outside the list goes to the form-level banner — a non-empty
 * path can still bind to nothing, which looks bindable but routes to a
 * control that doesn't exist.
 */
const KNOWN_WRITE_KEYS: readonly string[] = Object.keys(toFormValues());

/**
 * The values the form opens with: the record's own, with a locked company
 * overriding the selection. Deliberately NOT folded into the edit baseline
 * (see `baseline` below) — the baseline is what the record actually holds,
 * so a lock that differs from the saved company still counts as a change
 * and is sent.
 */
function startingValues(deal: Deal | undefined, lockedCompanyId: number | null): FormValues {
  const base = toFormValues(deal);
  return lockedCompanyId === null ? base : { ...base, companyId: String(lockedCompanyId) };
}

/**
 * The CREATE half of the remount key — what distinguishes one new deal from
 * the next when the modal is reopened without unmounting.
 *
 * ⚠️ **It is the CREATE half only, and the combined key at the call site is
 * kept deliberately.** `QuoteFormModal` keys on `quote?.id ?? createKeyOf(...)`
 * — an either/or — where this modal has always keyed on the record AND the lock
 * together. Anything relying on the edit key carrying the lock would break if
 * that were narrowed to match the quote form, so this function supplies a
 * segment rather than replacing the key.
 *
 * ⚠️ **It therefore takes NO `lockedCompany`, and that is a consequence of the
 * combined key rather than an omission.** The quote form's `createKeyOf` reads
 * the lock because there it IS the whole key; here the lock is already its own
 * segment at the call site, so a second copy would be a parameter this function
 * could only ever ignore — and `noUnusedParameters` is on, so an ignored one
 * does not compile. ⚠️ **If the call site is ever narrowed to an either/or, the
 * lock has to come back INTO this function**, or the key loses it entirely.
 */
function createKeyOf(prefill: DealFormPrefill | undefined): string {
  const lines = prefill?.lines ?? [];
  if (lines.length === 0) return '';
  /*
   * 🔴 **THE LINES ARE IN THE KEY, AND WITHOUT THEM THE SEED IS BROKEN.** Two
   * seeded raises for the SAME COMPANY agree on every other term this key
   * carries — the record is `new` both times and the lock is the same id — so
   * the key would be identical and the second open would reuse the first
   * mount, carrying the FIRST seed's basket. A deal raised from one seed and
   * filled with another's rows: correct-looking, against the right customer,
   * selling the wrong work, with nothing failing.
   *
   * ⚠️ **THE PROJECT IS IN THE FINGERPRINT AND IS THE MEMBER MOST EASILY
   * LEFT OUT.** Projects of interest are the whole reason this seam exists, so
   * the realistic pair is ONE COMPANY CHASED ON TWO PROJECTS — which agrees on
   * product, quantity and amount and differs ONLY here. Omitting it makes the
   * key identical for exactly the case the feature was built for.
   *
   * ⚠️ **Keyed on the CONTENT, never on the array's identity.** A caller maps
   * `plannedProductLines(row)` inline, so the array is a fresh object every
   * render; keying on identity would remount on every keystroke and throw away
   * whatever had been typed.
   *
   * ⚠️ **The PATTERN is the finding, not the field list.** `QuoteFormModal`
   * records this same sentence being left standing past its own subject twice —
   * once when a seed gained a price, once when it gained a project. **Any
   * member added to `PlannedProductLine` that a person can differ on belongs
   * here**, and the failure is always the same: a stale mount, a `201`, and
   * nothing wrong on screen.
   */
  return lines
    .map(
      (line) =>
        `${line.productId}x${line.quantity}@${line.plannedAmount ?? ''}#${line.project?.id ?? ''}~${line.cityIds?.join('.') ?? ''}`,
    )
    .join(',');
}

export function DealFormModal({
  open,
  onClose,
  deal,
  lockedCompany,
  prefill,
  /*
   * ⚠️ Defaulted HERE, once, so the inner form takes a plain `boolean` and no
   * branch further down has to decide what an absent flag means. Absent is
   * `false` — the rule as it stood for every caller but the convert flow.
   */
  allowCompanyAwaitingApproval = false,
  onSaved,
}: DealFormModalProps) {
  if (!open) return null;
  return (
    <DealForm
      // Keyed on the lock as well as the record: reopening the same closed
      // modal for a different company must not keep the previous seed. The
      // third segment is the SEED — see `createKeyOf`, and note it is empty on
      // every unseeded open, so this is byte-identical to the old two-segment
      // key for every caller that passes no `prefill`.
      key={`${deal?.id ?? 'new'}:${lockedCompany?.id ?? ''}:${createKeyOf(prefill)}`}
      onClose={onClose}
      deal={deal}
      lockedCompany={lockedCompany}
      prefill={prefill}
      allowCompanyAwaitingApproval={allowCompanyAwaitingApproval}
      onSaved={onSaved}
    />
  );
}

function DealForm({
  onClose,
  deal,
  lockedCompany,
  prefill,
  allowCompanyAwaitingApproval,
  onSaved,
}: {
  onClose: () => void;
  deal?: Deal;
  lockedCompany?: { id: number; name: string };
  prefill?: DealFormPrefill;
  /**
   * Already defaulted by {@link DealFormModal}, so `boolean` rather than
   * `boolean | undefined`. See {@link DealFormModalProps.allowCompanyAwaitingApproval}
   * for who may pass it, why the convert path is different, and the cost.
   */
  allowCompanyAwaitingApproval: boolean;
  onSaved: (deal: Deal) => void;
}) {
  const formId = useId();
  const nameRef = useRef<HTMLElement | null>(null);
  const isEdit = deal !== undefined;

  const lockedCompanyId = lockedCompany?.id ?? null;

  const baseline = useMemo(() => toFormValues(deal), [deal]);
  const [values, setValues] = useState<FormValues>(() => startingValues(deal, lockedCompanyId));
  const [clientErrors, setClientErrors] = useState<Record<string, string>>({});
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  /*
   * Asked only on an EDIT that moves the deal onto an `isLost` stage (the flag,
   * never the name) — see `movingToLost`. Sent in the same PATCH as `stageId`.
   */
  const [lostReasonId, setLostReasonId] = useState('');
  const [lostNote, setLostNote] = useState('');

  /*
   * The line items to write after the deal exists — CREATE ONLY. Lines hang
   * off `POST /deals/{id}/products`, so there is nothing to attach them to
   * until the deal is saved. `DealLineItemsTable` in `draft` mode holds the
   * rows, issues no request, and hands up the exact POST bodies via the same
   * `createPayload()` its saved mode uses.
   *
   * On EDIT the grid owns its own writes, so this array stays empty and
   * `handleSubmit` never reads it.
   */
  const [draftLines, setDraftLines] = useState<DealProductCreateInput[]>([]);
  /**
   * How many rows the grid is WITHHOLDING because they would be refused.
   *
   * ⚠️ `draftLines.length` cannot see this — a blocked row is absent from
   * the array, so counting it would report a grid full of faults as
   * "nothing to write" and create the deal without them, against rows the
   * person can no longer see. Save is disabled on this instead.
   */
  const [blockedDraftRows, setBlockedDraftRows] = useState(0);
  /**
   * What the EDIT-mode grid is holding — unsaved rows, and how many of those it
   * would refuse to send.
   *
   * ⚠️ **A disabled control is a CLAIM, and this one used to be false.** The
   * footer Save was disabled on `!isDirty` alone, where dirty means the HEADER
   * form — so somebody who edited only line items met a greyed-out "Save
   * changes" beside rows each reading "Unsaved", and `handleSubmit` told them
   * *"Nothing has changed yet."* if they reached it. `QuoteFormModal` carries
   * the same pair of fields for the same reason; the audit log caught it there
   * on 2026-09-01.
   *
   * Zero on CREATE: the draft grid reports through `onDraftBlockedChange`
   * instead, because a draft row has nowhere to be saved to.
   */
  const [lineEdits, setLineEdits] = useState<{ dirty: number; blocked: number }>({
    dirty: 0,
    blocked: 0,
  });
  /**
   * The grid's own save run, handed up by `registerSave`.
   *
   * ⚠️ **`null` means THERE IS NO GRID — never "nothing to save".** It is null
   * on a create (the draft grid registers nothing, because a draft row has
   * nowhere to be saved to) and null once the grid unmounts. Read it beside
   * `lineEdits.dirty`, never instead of it.
   */
  const saveLinesRef = useRef<(() => Promise<boolean>) | null>(null);
  /*
   * Stable, so the grid's registration effect runs on mount and unmount rather
   * than on every render of this form. An inline arrow here would re-register
   * continuously — harmless, but it would make the effect's dependency list a
   * lie about when registration happens.
   */
  const registerLineSave = useCallback((save: (() => Promise<boolean>) | null) => {
    saveLinesRef.current = save;
  }, []);
  /**
   * Set only when the deal SAVED and at least one of its products did not.
   * The modal stops being a form at that point — the write already
   * happened — so `onSaved` fires when the report is dismissed; every
   * caller closes the modal from inside `onSaved`.
   */
  const [outcome, setOutcome] = useState<{
    deal: Deal;
    /**
     * ⚠️ Drives the wording only — "was created" against "was saved". The
     * report exists on BOTH paths now, because a Next task note is written on
     * every change and an edit's note can be refused with the PATCH already
     * applied.
     */
    created: boolean;
    failures: LineFailure[];
    attached: number;
    total: number;
    noteFailures: NextTaskNoteFailure[];
    /*
     * The reminder is reported SEPARATELY from the notes rather than folded
     * into that list, because the list renders each entry under a hardcoded
     * "Note on ..." heading. A task failure shown under it would be captioned
     * as a note that was not recorded -- a confidently wrong sentence about
     * the one thing on this screen that is already going wrong.
     */
    reminderFailure: NextTaskReminderFailure | null;
  } | null>(null);

  const pickers = useDealPickers();
  /*
   * The currency master. `enabled` is unconditional: this inner form only
   * exists while the modal is open — the wrapper returns null otherwise — so
   * there is no mounted-but-hidden case to defer for.
   *
   * ⚠️ `failed` is surfaced rather than swallowed. An unreadable master renders
   * as an empty dropdown, which reads as "there are no currencies" and is one
   * save away from dropping a code the deal is carrying.
   */
  const { currencies, loading: currenciesLoading } = useCurrencyOptions(true);
  /*
   * ⚠️ NARROWED to the account-lead group — and this REVERSES the note that
   * stood here, so the reversal is written down rather than edited away.
   *
   * That note said `assertLeadsInGroups` ran in the COMPANIES and CONTACTS
   * services only and that deals checked existence alone, so narrowing here
   * would hide valid choices. **That was measured wrong.**
   * `assertInGroup('accountLeadId', …)` runs in the deals, quotes and
   * invoices services too, so naming a non-member as a deal's account-lead
   * override is a live 422 — and an un-narrowed picker was OVER-offering
   * into it: the reader picked a colleague, saved, and collected a refusal.
   *
   * The old note's REASONING survives its own conclusion and is why the two
   * degradations below are not optional: over-restricting a picker
   * client-side fails silently, and the person who meets it adds a colleague
   * to the group to "fix" a restriction — quietly widening a group that
   * enforces elsewhere. So narrowing is only ever done on evidence.
   *
   * ⚠️ Narrowing is PER FIELD, never per fetch. `users` — the whole directory
   * — is deliberately not destructured here, because this form's only staff
   * picker is the account-lead override. Were an owner picker ever added it
   * would read `users` and NOT `byGroup`: `ownerId` is checked by no server
   * guard and there is no owner group to check it against.
   */
  const { loading: usersLoading, users, byGroup } = useUserOptions(true, true);
  /*
   * ⚠️ **A group master that did not load narrows NOTHING.**
   * `narrowStaffToGroup` resolves the slug against the master and, finding no
   * row — a 404, a failed read, a slug the master does not carry — hands back
   * the whole directory with `narrowed: false`. That is the deliberate
   * degradation, and it is the right one: an unreadable master is not
   * evidence that nobody may lead a deal, and a picker emptied on the
   * strength of a request that never came back would be a dead end on every
   * deal in the CRM. The server refuses the bad name either way, and its 422
   * says who MAY hold the role — a truthful refusal beats a guessed empty
   * picker.
   *
   * `narrowed: false` also silences `narrowingNote` and `outsideGroupNote`
   * below, so no sentence claims a restriction we could not confirm.
   */
  const leadStaff = byGroup[USER_GROUP_SLUGS.accountLead];

  /*
   * ── The Next task's date and assignee ────────────────────────────────────
   *
   * DELIBERATELY NOT IN `FormValues`. That interface is what `KNOWN_WRITE_KEYS`
   * is derived from and what `buildPayload` reads, so a key added there is a
   * key this form SENDS -- and `d_deals` has no column for either of these.
   * Against a `.strict()` schema that is a 422 whose `path` is the empty
   * string, which routes to no control and renders as a form-level refusal
   * nobody can act on. These two drive exactly one thing: whether a task is
   * raised after the deal is saved.
   */
  const [taskDueDate, setTaskDueDate] = useState('');
  const [taskAssigneeId, setTaskAssigneeId] = useState('');

  /*
   * EVERY member of staff, not `leadStaff`. Being allowed to lead an account
   * and being someone a colleague may hand a follow-up to are different
   * questions, and the server enforces a group on the first and nothing at all
   * on the second. Narrowing here would invent a restriction the API does not
   * have.
   */
  const assigneeOptions = useMemo<ComboboxOption[]>(
    () =>
      users.map((person) => ({
        value: String(person.id),
        label: person.name.trim() !== '' ? person.name : person.email,
        ...(person.name.trim() !== '' ? { hint: person.email } : {}),
      })),
    [users],
  );

  /**
   * Raises the reminder for a save that has already landed.
   *
   * Never throws -- see `createNextTaskReminder`. A rejected promise here would
   * reach the caller's `catch` and be rendered as a FAILED save of a deal that
   * is sitting in the list, and somebody who reads "failed" creates it again.
   */
  async function raiseReminderFor(
    saved: Deal,
    text: string,
  ): Promise<NextTaskReminderFailure | null> {
    if (!shouldRaiseReminder(text, taskDueDate, taskAssigneeId)) return null;
    return createNextTaskReminder({
      dealId: saved.id,
      title: text,
      dueDate: taskDueDate,
      assigneeId: taskAssigneeId,
      /*
       * Resolved from the option list so a refusal names a person. Falls back
       * to "you" for the empty case, which is what an omitted assignee means:
       * the server defaults the task to the caller.
       */
      assigneeName:
        assigneeOptions.find((option) => option.value === taskAssigneeId)?.label ?? 'you',
    });
  }

  /*
   * ── The CONTACT: scoped to the company, seeded from its primary ──────────
   *
   * ⚠️ **Touched starts TRUE on an edit, and that is not a long way of writing
   * `isEdit`.** A stored contact is the record's own, and so is a stored
   * ABSENCE: a deal deliberately left with no contact must not gain one because
   * somebody opened the form. Both are decisions the seed may not overturn —
   * which is why the flag is the record's existence rather than its
   * `contactId`, and why `onCompanyChange` is the one place that hands the field
   * back (see there).
   */
  const contactTouched = useRef(isEdit);
  /**
   * The company this form OPENED on, so a stored contact can be pinned for it
   * and only for it.
   *
   * ⚠️ Captured at mount rather than compared against `baseline.companyId`,
   * which is the blank `''` on a create — a create opened from a company would
   * then read as "the company changed" from its first frame and announce a
   * clearing that never happened.
   *
   * ⚠️ `useState` rather than `useRef`, though nothing ever writes it: it is
   * READ DURING RENDER, and a ref read during render is exactly the value React
   * does not re-render for.
   */
  const [openedCompanyId] = useState(values.companyId);
  const companyMoved = values.companyId !== openedCompanyId;
  const companyIdNumber = Number(values.companyId.trim());
  /** The company the contact list is scoped to; `null` when none is chosen. */
  const contactCompanyId =
    !Number.isInteger(companyIdNumber) || companyIdNumber <= 0 ? null : companyIdNumber;
  /*
   * Pinned to the locked company when there is one, so the seeded value
   * carries a name with no lookup, and the search isn't run at all since the
   * picker it feeds isn't rendered — the limiter is shared office-wide.
   */
  const companies = useLookupSearch(
    'companies',
    lockedCompany ?? deal?.company ?? null,
    lockedCompanyId === null,
    // Not a scoped picker — that argument belongs to contacts alone.
    undefined,
    /*
     * CREATE only: a new deal may only name an APPROVED company, so the
     * unapproved ones are withheld and counted in the picker's footnote. Edit
     * offers the whole directory, unchanged — see `withholdUnapproved`.
     */
    !isEdit,
  );

  /*
   * ⚠️ No product search on this form any more. `DealLineItemsTable` fetches
   * the catalogue itself, once for the whole grid — a second fetch here
   * would duplicate a read off the shared limiter.
   */
  const { ability } = useAuth();

  /*
   * Who the CHOSEN company says leads it — the person the empty option
   * names. Read off the company being chosen, not off the deal, so changing
   * the company changes what a cleared picker falls back to. The deal's own
   * `companyAccountLead` seeds the case where the selection is still the
   * record's own company, at no extra request.
   */
  const companyLead = useCompanyAccountLead(
    values.companyId,
    deal?.company ? { companyId: String(deal.company.id), lead: deal.companyAccountLead } : null,
  );

  /*
   * ⚠️ The stored pin is withheld once the company has MOVED — the same rule
   * `intakeOptions` applies to a stored intake across a cycle change, and for a
   * sharper reason: there the stale choice is a guaranteed 422, here it is a
   * guaranteed silent success, because `deals.service.ts` checks a contact for
   * EXISTENCE and never against the company.
   *
   * ⚠️ The SEED needs pinning exactly as a stored contact does:
   * `values.contactId` holding an id selects nothing until an option carries
   * that value, and the scoped list has not landed at the moment the seed is
   * applied — so an unpinned seed renders "Unassigned" over a form that is in
   * fact carrying the id. It is read straight off `companyLead`, which already
   * discards a stale answer during render, so nothing here has to be cleared
   * when the company moves: there is no second copy to forget.
   */
  const storedContactPin = companyMoved ? null : (deal?.contact ?? null);
  const contactPin = storedContactPin ?? companyLead.primaryContact;
  /*
   * ⚠️ SCOPED. `contactCompanyId` is `null` when the form names no company, and
   * the picker is then EMPTY rather than falling back to the whole directory —
   * an unscoped list is the behaviour the owner asked to remove, and it is the
   * only way to pick a contact the company will contradict. `contactHint` says
   * why. Same shape as the intake picker's dependence on the cycle.
   */
  const contacts = useLookupSearch('contacts', contactPin, true, contactCompanyId);
  /** Empty = following the company. See the `FormValues.accountLeadId` note. */
  const accountLeadOverridden = values.accountLeadId !== '';
  const hasCompany = values.companyId !== '';
  /*
   * Appears once a company is chosen, and ALSO for a deal that already
   * holds an override with no company — hiding that would leave a real
   * value with nowhere to be seen or cleared. Cannot appear spuriously on a
   * create: nothing can populate this field before it exists on screen.
   */
  const showAccountLead = hasCompany || accountLeadOverridden;
  const followLabel = followOptionLabel(hasCompany, companyLead);

  /*
   * ── The default deal name ───────────────────────────────────────────────
   * `<company short name> - Plans - <current cycle>`, from
   * `src/lib/document-naming.ts` — shared with the quote form.
   *
   * ⚠️ CREATE ONLY. A stored name is the record's own; re-deriving it would
   * rename a deal that may already have been discussed with a customer.
   * `isEdit` guards both the seed and the cycle fetch.
   *
   * ⚠️ Never over something a person typed. `nameTouched` is written only by
   * the Deal name input's own `onChange`, never inferred from non-emptiness
   * — that test would disarm itself the moment it fired and then the first
   * company change would silently rewrite typing.
   */
  const { cycles, loading: cyclesLoading } = useCycleOptions(!isEdit);
  const nameTouched = useRef(false);
  /*
   * ⚠️ Both start TRUE on an edit, and that is not a long way of writing
   * `isEdit` — it is the same rule as `contactTouched` above. A stored currency
   * and a stored stage are the record's own, and so is a stored ABSENCE: a deal
   * deliberately left with no currency must not acquire one because somebody
   * opened the form to fix a typo in the name. On a CREATE they start false and
   * the seeds below fill the fields until the first gesture.
   *
   * ⚠️ Written ONLY by the controls' own handlers, never inferred from
   * non-emptiness — that test disarms itself the moment it fires, and the next
   * company change would then silently rewrite a chosen value.
   */
  const currencyTouched = useRef(isEdit);
  const stageTouched = useRef(isEdit);
  /**
   * The code this form put in the box on the company's behalf, kept so the
   * option list can offer it back.
   *
   * ⚠️ Not bookkeeping. A company's default may be a code our 164-row master
   * does not carry — the legal set is Xero's — and an option list missing the
   * value it is showing renders the picker BLANK over a form that is in fact
   * carrying the code. That reads as "no currency" and the next save writes the
   * emptiness back. Same shape as the contact pin.
   */
  const [seededCurrency, setSeededCurrency] = useState<string | null>(null);
  /*
   * Primitives, not the `naming` object, so the effect re-runs only when the
   * company actually changes — the hook rebuilds its return value each time.
   */
  const seedCompanyName = companyLead.naming?.name ?? null;
  const seedCompanyShortName = companyLead.naming?.shortName ?? null;

  useEffect(() => {
    if (isEdit || nameTouched.current) return;
    /*
     * Wait for the cycle list to settle — seeding first and re-seeding a
     * moment later would rewrite the box under the reader's cursor.
     * `loading` also goes false on a FAILED fetch, which is fine here: an
     * unreadable cycle master just means no cycle segment.
     */
    if (cyclesLoading) return;

    const next = defaultDocumentName(
      seedCompanyName === null ? null : { name: seedCompanyName, shortName: seedCompanyShortName },
      currentCycle(cycles),
    );
    /*
     * `''` means nothing worth building from — leave the field alone rather
     * than writing an empty or half-built value. Covers clearing the
     * company too: the box keeps what's in it.
     */
    if (next === '') return;
    /*
     * oxlint's `set-state-in-effect` fires here; deriving during render
     * cannot apply — this is a DEFAULT, not a derived value, since the
     * moment somebody types the box stops following the company for good.
     * The updater returns `prev` unchanged when the value matches, so the
     * extra pass happens once per company change, not every render.
     */
    // eslint-disable-next-line react/set-state-in-effect
    setValues((prev) => (prev.dealName === next ? prev : { ...prev, dealName: next }));
  }, [isEdit, seedCompanyName, seedCompanyShortName, cycles, cyclesLoading]);

  /*
   * ── The default CONTACT: the company's designated primary ───────────────
   *
   * ⚠️ **NOT gated on `isEdit`, unlike the deal-name seed above it, and that
   * difference is the whole design.** The name seed is create-only because a
   * stored name may already have been quoted to a customer. A contact is
   * different: on an edit this can only fire after `onCompanyChange` has reset
   * `contactTouched`, which happens exactly when somebody moved the company —
   * the moment the stored contact stopped describing this deal. It is not a
   * re-derivation of a saved record; the field it fills was emptied one gesture
   * ago, by the user.
   *
   * ⚠️ `primaryContact: null` seeds NOTHING and is an ordinary state — an
   * account that designates nobody, or one that could not be read. Never an
   * instruction to clear, which is why the branch only ever writes an id.
   *
   * `companyLead.primaryContact` rides on the account-lead read, so this costs
   * no extra request and can never describe a different company from the one
   * `values.companyId` names — the hook discards a stale answer during render.
   */
  const seedPrimaryContact = companyLead.primaryContact;
  useEffect(() => {
    if (seedPrimaryContact === null || contactTouched.current) return;
    /*
     * oxlint's `set-state-in-effect` fires here, as it does on the deal-name
     * seed above; deriving during render cannot apply for the same reason —
     * this is a DEFAULT, not a derived value, and the moment somebody picks or
     * clears the box it stops following the company for good. The updater
     * returns `prev` unchanged when the field is not empty, so the extra pass
     * happens once per company change rather than every render.
     */
    // eslint-disable-next-line react/set-state-in-effect
    setValues((prev) => {
      // Re-checked inside the updater: a pick made between the render that
      // scheduled this and the render that applies it must still win.
      if (contactTouched.current || prev.contactId !== '') return prev;
      return { ...prev, contactId: String(seedPrimaryContact.id) };
    });
  }, [seedPrimaryContact]);

  /*
   * ── The default CURRENCY: the company's own ───────────────────────
   *
   * ⚠️ Checked against `CURRENCY_PATTERN` BEFORE it lands. The server folds no
   * case and refuses anything that is not exactly three capital letters, so a
   * code the company holds in some other shape is dropped here rather than
   * seeded into a guaranteed 422 on a field the person never touched.
   *
   * ⚠️ `null` seeds NOTHING and is never an instruction to clear — an account
   * that names no default and one that could not be read are the same outcome
   * here, so the branch only ever writes a code.
   *
   * Rides on the `getCompany` read the account lead already makes, so it costs
   * no extra request and can never describe a different company from the one
   * `values.companyId` names.
   */
  const seedCurrency = companyLead.currency;
  useEffect(() => {
    if (seedCurrency === null || currencyTouched.current) return;
    /*
     * oxlint's `set-state-in-effect` fires here as it does on the two seeds
     * above, and for the same reason it cannot be derived during render: this
     * is a DEFAULT, and the moment somebody picks or clears the box it stops
     * following the company for good.
     */
    // eslint-disable-next-line react/set-state-in-effect
    setSeededCurrency(seedCurrency);
    // eslint-disable-next-line react/set-state-in-effect
    setValues((prev) => {
      // Re-checked inside the updater: a pick made between the render that
      // scheduled this and the render that applies it must still win.
      if (currencyTouched.current || prev.currency === seedCurrency) return prev;
      return { ...prev, currency: seedCurrency };
    });
  }, [seedCurrency]);

  /*
   * ── The default STAGE: the first stage of the pipeline ───────────────
   *
   * ⚠️ **Resolved by the LOWEST `sortOrder` among ACTIVE stages, NEVER by
   * matching the name "New Deal".** `f_deal_stages` is admin-writable through
   * `/api/v1/deal-stages` precisely so a stage can be renamed or added without a
   * deploy, so a rule keyed on the label is a rule an ordinary edit breaks — and
   * it breaks silently, leaving every new deal with no stage, which reads as a
   * person forgetting to pick one.
   *
   * ⚠️ Nor by taking the first row of the list. The order the endpoint happens
   * to return is not a contract this form can hold, and `sortOrder` is the
   * column that MEANS pipeline position — the same column the board draws its
   * columns from. Retired stages are excluded: a deal must not open on a stage
   * the picker itself would not offer.
   *
   * ⚠️ CREATE ONLY, via `stageTouched`. A tie cannot be broken meaningfully,
   * so `<` keeps the FIRST row seen at the lowest value — stable for a given
   * response rather than arbitrary from one render to the next.
   */
  const firstStageId = useMemo(() => {
    let first: DealStage | null = null;
    for (const stage of pickers.stages) {
      if (!stage.isActive) continue;
      if (first === null || stage.sortOrder < first.sortOrder) first = stage;
    }
    return first === null ? null : String(first.id);
  }, [pickers.stages]);

  /**
   * An edit that moves the deal onto a stage carrying `isLost`. Create is not
   * asked: `POST /deals` takes no lost reason.
   */
  const movingToLost = useMemo(() => {
    if (!isEdit || values.stageId === '' || values.stageId === baseline.stageId) return false;
    return pickers.stages.find((stage) => String(stage.id) === values.stageId)?.isLost === true;
  }, [isEdit, values.stageId, baseline.stageId, pickers.stages]);

  useEffect(() => {
    if (isEdit || firstStageId === null || stageTouched.current) return;
    // eslint-disable-next-line react/set-state-in-effect
    setValues((prev) => {
      // Re-checked inside the updater, and `prev.stageId !== ''` is the second
      // guard: a stage chosen between scheduling and applying must survive.
      if (stageTouched.current || prev.stageId !== '') return prev;
      return { ...prev, stageId: firstStageId };
    });
  }, [isEdit, firstStageId]);

  /*
   * `values.contactId === ''` is part of the "cleared" sentence and not of the
   * pin gate: once somebody has chosen a replacement there is nothing to
   * explain, but the old contact stays off the list either way.
   */
  const contactScope = { ...contacts.scope, cleared: companyMoved && values.contactId === '' };

  /**
   * The chosen company as `{id, name}`, for the Contact field's inline
   * "+ Add a new contact…" row to lock a new contact to.
   *
   * ⚠️ The name is read from the SAME option list the company trigger draws its
   * own label from, so the two can never disagree — if the field on screen shows
   * a company, this resolves it, and if it shows nothing there is nothing to
   * lock to. That covers the locked mode as well, where no search runs and the
   * pinned row is the whole option list.
   */
  const contactCompany = useMemo(() => {
    if (contactCompanyId === null) return null;
    const label = companies.options.find((option) => option.value === values.companyId)?.label;
    return label === undefined ? null : { id: contactCompanyId, name: label };
  }, [contactCompanyId, companies.options, values.companyId]);

  /**
   * The scoped options, with the pinned contact MARKED when it is not one of
   * this company's.
   *
   * ⚠️ The marking is gated on `contactScopeIsAuthoritative`; the OPTION never
   * is. While the list is loading, unreadable, or narrowed by a search term,
   * every stored contact looks absent from it — and accusing a perfectly correct
   * contact of belonging to another account on the strength of a request that
   * has not come back is a confident wrong answer where saying nothing costs
   * nothing. Same gate as `productOptions`' "Retired" reason in
   * `ProjectFormModal`.
   */
  const markOffCompany = contactScopeIsAuthoritative(contactScope) && contacts.pinInjected;
  const pinnedContactValue = contactPin === null ? null : String(contactPin.id);
  const contactOptions = useMemo<ComboboxOption[]>(() => {
    if (!markOffCompany || pinnedContactValue === null) return contacts.options;
    return contacts.options.map((option) =>
      option.value === pinnedContactValue ? { ...option, hint: CONTACT_NOT_ON_COMPANY } : option,
    );
  }, [contacts.options, markOffCompany, pinnedContactValue]);

  /**
   * The currencies this picker may offer.
   *
   * The stored code is pinned back when the master no longer carries it — a
   * currency retired since the deal was priced must not render as blank. A
   * SEEDED code needs the same pin and needs it MORE: a company's default is
   * validated against Xero's set rather than ours, so it can perfectly well name
   * a code `/currencies` has never held. Offered only while it is still what the
   * field holds; once somebody picks something else it refers to nothing.
   */
  const currencyChoices = useMemo(
    () =>
      currencyOptions(
        currencies,
        deal?.currency ??
          (seededCurrency !== null && seededCurrency === values.currency ? seededCurrency : null),
      ),
    [currencies, deal, seededCurrency, values.currency],
  );

  const errors = useMemo(() => ({ ...clientErrors, ...serverErrors }), [clientErrors, serverErrors]);

  const changedKeys = useMemo(() => {
    const keys = Object.keys(baseline) as (keyof FormValues)[];
    return keys.filter((key) => values[key].trim() !== baseline[key].trim());
  }, [values, baseline]);
  const isDirty = changedKeys.length > 0;

  function setField(key: keyof FormValues, next: string) {
    setValues((prev) => ({ ...prev, [key]: next }));
    setClientErrors((prev) => (key in prev ? without(prev, key) : prev));
    setServerErrors((prev) => (key in prev ? without(prev, key) : prev));
  }

  /**
   * Changing the company CLEARS the contact, and hands the field back to the
   * primary-contact seed.
   *
   * ⚠️ **The server will not catch this one.** `deals.service.ts` checks a
   * contact through `contactsExist` — does the row exist — and never against the
   * company; the pair that `deliverables.service.ts` refuses is accepted here
   * with a `200`. So a contact left behind by a company change is not a 422
   * somebody reports, it is a stored, plausible, wrong person on the record a
   * quote and then an invoice are raised from, and this function is the only
   * thing standing in front of it.
   *
   * Resetting `contactTouched` is the second half and is what makes the seed
   * correct on an EDIT: the previous choice was a choice about the previous
   * company, so it stops counting the moment that company goes. Without it the
   * field would be cleared and then left empty, which is worse than either end —
   * the person loses a value AND is offered nothing in its place.
   *
   * ⚠️ Only on an actual CHANGE. Re-picking the same company must not discard a
   * contact somebody deliberately chose.
   *
   * ⚠️ **`companyId` and `contactId` do NOT have to travel together in the
   * PATCH**, unlike `{cycleId, intakeId}` or a deliverable's
   * `{companyId, pointOfContactId}`: no service here judges the pair on the
   * post-merge record, so a lone `companyId` is an ordinary write. `buildPayload`
   * sends whichever of the two actually moved, and that is correct — which is
   * exactly why the clearing has to happen HERE, in the form, where it is the
   * only guard there is.
   *
   * ⚠️ The account-lead override is deliberately NOT cleared alongside it. That
   * field means "depart from whatever company this deal names", and the person
   * it names is checked against the account-lead group rather than against the
   * company — so it survives a re-parenting exactly as it survives everything
   * else. Only the contact belongs to one company.
   */
  function onCompanyChange(next: string) {
    if (next === values.companyId) return;
    contactTouched.current = false;
    // Nothing to clear on the seed side: the primary is read off `companyLead`,
    // which is pinned to the company it was fetched for and stops resolving on
    // its own. One writer, no second place to forget.
    setValues((prev) => ({ ...prev, companyId: next, contactId: '' }));
    setClientErrors((prev) => without(without(prev, 'companyId'), 'contactId'));
    setServerErrors((prev) => without(without(prev, 'companyId'), 'contactId'));
  }

  /**
   * The ONLY writer of `contactTouched` besides `onCompanyChange` above.
   *
   * Set even when the box is CLEARED, because clearing is a decision: without
   * that, emptying the contact would hand the field straight back to the seeder,
   * which refills it on the next company change and reads as the control
   * fighting the user. Same rule as `nameTouched`.
   */
  function onContactChange(next: string) {
    contactTouched.current = true;
    setField('contactId', next);
  }

  /**
   * The ONLY writer of `currencyTouched`.
   *
   * Set even when the code is CLEARED and even when it is unchanged: both are
   * decisions, and a seed that overwrote either would be choosing a currency on
   * somebody's behalf on a record that prices work. Same rule as `nameTouched`.
   */
  function onCurrencyChange(next: string) {
    currencyTouched.current = true;
    setField('currency', next.toUpperCase());
  }

  /** The ONLY writer of `stageTouched`. Clearing counts, as everywhere else. */
  function onStageChange(next: string) {
    stageTouched.current = true;
    setField('stageId', next);
  }

  /*
   * The response carries resolved objects where the write takes ids, and
   * read-only columns besides, so a loaded deal can never be PATCHed back
   * wholesale — every schema is `.strict()`. Only changed keys go. The
   * acting user is never sent; the server takes it from the bearer token.
   *
   * ⚠️ `projectId` is never sent, on create OR edit, and its absence is
   * load-bearing. The form no longer offers a project, but an existing deal
   * may still HAVE one, and its `cities` are derived through it. An omitted
   * key is left untouched while an explicit `null` CLEARS it — so emitting
   * `projectId: null` merely because no control fills it would silently
   * wipe a project nobody asked to remove. This is guaranteed structurally:
   * `projectId` is not a `FormValues` key, so it can never appear in
   * `changedKeys`, and it is not in `ID_KEYS`, so the POST loop never reads
   * it.
   *
   * ⚠️ `ownerId` is in exactly that position, for exactly that reason. Owner
   * was removed from the deals UI outright, but the column still holds
   * whatever it held — emitting `ownerId: null` because no control fills it
   * would wipe every deal's owner on the next unrelated save. It left
   * `FormValues` and `ID_KEYS` with the control, which is what makes the key
   * structurally unsendable. Do not add it back to either list without
   * adding a control that says what is written.
   *
   * `accountLeadId` took its place and IS sent: `''` becomes `null`, which
   * CLEARS THE OVERRIDE and returns the deal to following its company —
   * not "no account lead"; see `followOptionLabel`.
   *
   * ⚠️ **`dealTypeId` and `leadSourceId` joined that list on 2026-08-30 and are
   * now in the identical position — three keys, not one.** Both controls were
   * removed from this modal on the owner's decision, so both keys left
   * `FormValues` and `ID_KEYS` with them. The reasoning is written out at
   * `ID_KEYS`; the reason it is cross-referenced from HERE is that this comment
   * is the one somebody reads while wondering why a key they can see on the
   * record never reaches the payload, and a list of two reads as exhaustive.
   *
   * ⚠️ **`probability` joined that list on 2026-08-31, and it is the one key
   * here that is gone for good rather than merely unedited.** The owner's
   * decision is that a deal's probability is ALWAYS its stage's, so the deal's
   * own override column is not a thing this app sets at all — the control went,
   * and the key left `FormValues` with it. Note that this makes it unsendable
   * TWICE over, and both halves matter: it cannot reach `changedKeys` on an
   * edit, and it cannot reach the create payload either. The server still
   * ACCEPTS `probability` on both verbs (`deals.schema.ts` declares it
   * `nullish()`), so nothing but this absence stops a payload carrying one —
   * which is exactly why the absence had to be structural rather than a
   * `delete payload.probability` somebody can drop.
   */
  function buildPayload(): DealCreateInput | DealUpdateInput {
    if (deal) {
      const payload: DealUpdateInput = {};
      for (const key of changedKeys) {
        const next = values[key].trim();
        if (key === 'dealName') {
          // Not nullable server-side: `dealName: null` is a 422.
          payload.dealName = next;
        } else if ((ID_KEYS as readonly string[]).includes(key)) {
          payload[key as (typeof ID_KEYS)[number]] = next === '' ? null : Number(next);
        } else if (key === 'amount') {
          payload.amount = next === '' ? null : next;
        } else if (key === 'currency') {
          payload.currency = next === '' ? null : next.toUpperCase();
        } else if (key === 'closingDate') {
          payload.closingDate = next === '' ? null : next;
        } else {
          payload[key as (typeof TEXT_KEYS)[number]] = next === '' ? null : next;
        }
      }
      if (movingToLost) {
        const note = lostNote.trim();
        payload.lostReasonId = Number(lostReasonId);
        payload.lostNote = note === '' ? null : note;
      }
      return payload;
    }

    const payload: DealCreateInput = { dealName: values.dealName.trim() };
    for (const key of ID_KEYS) {
      if (values[key] !== '') payload[key] = Number(values[key]);
    }
    if (values.amount.trim() !== '') payload.amount = values.amount.trim();
    if (values.currency.trim() !== '') payload.currency = values.currency.trim().toUpperCase();
    if (values.closingDate !== '') payload.closingDate = values.closingDate;
    for (const key of TEXT_KEYS) {
      const next = values[key].trim();
      if (next !== '') payload[key] = next;
    }
    return payload;
  }

  /*
   * ⚠️ Every rule below is imported, never re-derived — `InlineDealFields`
   * checks the same five columns and calls the same functions, so the two
   * editors cannot refuse the same value in two different sentences.
   *
   * The trimmed value is passed in because this form trims into the
   * PAYLOAD too; the validators themselves trim nothing.
   */
  function validate(): Record<string, string> {
    const found: Record<string, string> = {};

    const nameError = validateDealName(values.dealName.trim());
    if (nameError) found.dealName = nameError;

    const amountError = validateDealAmount(values.amount.trim());
    if (amountError) found.amount = amountError;

    const currencyError = validateDealCurrency(values.currency.trim().toUpperCase());
    if (currencyError) found.currency = currencyError;

    const closingDateError = validateDealClosingDate(values.closingDate.trim());
    if (closingDateError) found.closingDate = closingDateError;

    if (movingToLost) {
      if (lostReasonId === '') found.lostReasonId = 'Choose why the deal was lost.';
      if (lostNote.trim().length > 500) found.lostNote = 'Must be 500 characters or fewer.';
    }

    /*
     * ⚠️ CREATE ONLY — the owner's rule is about RAISING a deal.
     *
     * An existing deal is left alone in both directions: one holding no company
     * (24 of the 40 live deals, measured 2026-08-31) stays editable, and one
     * whose company has since been rejected stays editable too. Neither is a
     * record somebody is raising, and refusing to save an unrelated field on
     * either would strand data over a rule that was never applied to it.
     *
     * ⚠️ This is the BACKSTOP, not the mechanism. The picker already withholds
     * unapproved companies on create — but `approvalStatus` can move between
     * the list landing and this submit, `lockedCompany` bypasses the picker
     * entirely, and the server enforces none of it. Same reasoning this app
     * gives for every permission-gated control: the hidden option is the
     * optimisation, the check is the guarantee.
     *
     * 🔴 **And since 2026-09-20 one caller may proceed on a company still
     * AWAITING approval** — the convert flow, which created it seconds ago. The
     * flag is passed through rather than tested here, so `rejected` and the
     * missing-company sentence are unaffected on every path; see
     * {@link DealFormModalProps.allowCompanyAwaitingApproval}.
     */
    if (!isEdit) {
      const companyError = newDealCompanyError(
        values.companyId,
        companyLead.approvalStatus,
        allowCompanyAwaitingApproval,
      );
      if (companyError) found.companyId = companyError;
    }

    for (const key of TEXT_KEYS) {
      if (values[key].trim().length > MAX_LENGTHS[key]) {
        found[key] = `Must be ${MAX_LENGTHS[key]} characters or fewer.`;
      }
    }

    return found;
  }

  /**
   * Writes the grid's rows against a deal that now exists. **Never throws.**
   *
   * ⚠️ One request per line, sequential, and it does NOT stop at the first
   * failure — there is no bulk endpoint and no transaction across them.
   * Sequential because the basket reads back in a fixed order, and a
   * `(product, project)` clash between two rows must be reported against
   * the second rather than raced. ⚠️ *SUPERSEDED, a THIRD reason that no
   * longer holds: "the office shares a 120-per-60s limiter".* It is
   * **12,000/60s** (corrected 2026-09-17), so the rate-limit reason
   * collapses entirely — the two above do not, and they are what keeps
   * this loop sequential. A refused line does not abandon the rest — the
   * lines are independent.
   *
   * ⚠️ The bodies are the GRID's, passed through untouched. That's what
   * keeps `unitPrice` OMITTED under `inherit` mode (the only way to say
   * "snapshot the catalogue price" — a `0` would overwrite a real price with
   * nothing), and keeps `inclusions`/`cityIds` absent rather than `[]` on a
   * hidden row, where `[]` would CLEAR a stored set.
   *
   * Nothing is rolled back and nothing pretends to be — the deal exists
   * from the moment create returned.
   */
  async function attachLines(
    dealId: number,
    rows: DealProductCreateInput[],
  ): Promise<LineFailure[]> {
    const failures: LineFailure[] = [];

    for (const [index, body] of rows.entries()) {
      try {
        await api.createDealProduct(dealId, body);
      } catch (caught) {
        failures.push({ position: index + 1, message: lineFailureMessage(caught) });
      }
    }

    return failures;
  }

  /**
   * The **Next task** text to record as a note on this save, or `''` for
   * "nothing to record". The rule is `nextTaskNoteText`'s; this only supplies
   * the two sides — what is typed now, and `baseline`, the value LOADED FROM
   * THE RECORD. On create the baseline is `''`, so any typed text is a change.
   */
  function noteTextForThisSave(): string {
    return nextTaskNoteText(values.nextStep, baseline.nextStep);
  }

  /**
   * Writes the Next task notes for this save.
   *
   * ⚠️ The company id comes from the FORM, not from `saved.company`. It is the
   * id the payload carried, so it is right even on the save that moved the deal
   * — and it cannot be silently dropped by a create response that resolves an
   * embedded object differently from a read (the same doubt `rereadDeal`
   * records). `saved.dealName` is the server's own, so the company note names
   * the deal as it was actually stored.
   *
   * Everything else — the sequencing, the no-company case, the never-throws
   * guarantee — is `writeNextTaskNotes`', shared with the inline editor.
   */
  function writeNotesFor(saved: Deal, text: string): Promise<NextTaskNoteFailure[]> {
    const companyId = values.companyId === '' ? null : Number(values.companyId);
    return writeNextTaskNotes(
      { dealId: saved.id, dealName: saved.dealName, companyId },
      text,
    );
  }

  /**
   * Re-reads the deal once a line has attached, so the caller gets the
   * record as it now is.
   *
   * ⚠️ Not to pick up `project`/`cities` — those come from the deal's own
   * header project, which this form never sets, so they don't move when a
   * line is written. The call exists because it is unverified whether
   * `POST /deals` and `GET /deals/{id}` resolve every embedded field
   * identically (`companyAccountLead` and other joined objects are the
   * plausible difference), and the cost is asymmetric: a needless re-read is
   * one request, taken only when a line attached; a missing one hands the
   * list a record with a hole in it. If the two responses are confirmed to
   * agree, delete this.
   *
   * A failed re-read is not a failed save — the create response stands in.
   */
  async function rereadDeal(created: Deal): Promise<Deal> {
    try {
      const fresh = await api.getDeal(created.id);
      return fresh.data;
    } catch {
      return created;
    }
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();

    const found = validate();
    setClientErrors(found);
    setServerErrors({});
    setFormError(null);
    if (Object.keys(found).length > 0) return;
    /*
     * ⚠️ The grid validates its own rows and withholds the bad ones — no
     * duplicate line validation here. This form only refuses to CREATE while
     * rows are being withheld, since once the deal exists those rows are
     * gone from a screen that has closed.
     */
    if (!isEdit && blockedDraftRows > 0) {
      setFormError(
        `${blockedDraftRows} line ${blockedDraftRows === 1 ? 'item' : 'items'} below ${
          blockedDraftRows === 1 ? 'is' : 'are'
        } not ready and would not be written with the deal. Fix or remove ${
          blockedDraftRows === 1 ? 'it' : 'them'
        } first — the deal cannot be un-created afterwards.`,
      );
      return;
    }

    const payload = buildPayload();
    /*
     * ⚠️ **"Nothing has changed" IS A CLAIM ABOUT THE WHOLE FORM, so it may not
     * be made from the header payload alone.** An edit that touched only line
     * items builds an empty payload and is not an empty edit; saying otherwise
     * here is the same falsehood the disabled button used to tell, one layer
     * down, and it would survive every gate above it.
     */
    const linesToSave = isEdit && lineEdits.dirty > 0;
    if (Object.keys(payload).length === 0 && !linesToSave) {
      setFormError('Nothing has changed yet.');
      return;
    }

    /*
     * Read BEFORE the write, from the values the payload was built from, so
     * nothing that happens during the save can change what is recorded.
     */
    const noteText = noteTextForThisSave();

    setSubmitting(true);
    try {
      if (deal) {
        /*
         * ⚠️ **LINES FIRST, HEADER SECOND — matching `QuoteFormModal` and
         * `InvoiceFormModal`, though NOT for their reason.** There the ordering
         * is arithmetic: a line write rewrites the document's four header
         * totals inside its own transaction, so header-first would let it
         * overwrite figures somebody had just typed. **A deal has no such
         * totals** — `amount` is hand-entered and the basket never maintains
         * it — so nothing here can be overwritten. What the order buys on a
         * deal is that the grid's own per-row report is on screen before
         * anything after it can fail, and that the three forms cannot drift
         * into three different sequences.
         *
         * ⚠️ **The grid still reports its own run, row by row, and this must
         * not restate it.** What is added here is only the ONE thing the grid
         * cannot know: whether the form may close.
         */
        const linesLanded = linesToSave ? ((await saveLinesRef.current?.()) ?? false) : true;
        const headerChanged = Object.keys(payload).length > 0;
        /*
         * Line items only — there is no PATCH to make, so the deal as it stands
         * IS the current record. That is a fact about deals rather than a
         * shortcut: a line write rewrites nothing on `d_deals`, so there is no
         * fresher copy to go and read.
         */
        const savedDeal = headerChanged ? (await api.updateDeal(deal.id, payload)).data : deal;
        /*
         * The PATCH has landed. Whatever the notes do, the deal is saved —
         * so a note failure becomes a REPORT, never a thrown error, and the
         * word "saved" leads it.
         */
        const editNoteFailures = await writeNotesFor(savedDeal, noteText);
        const editReminderFailure = await raiseReminderFor(savedDeal, noteText);
        if (linesLanded && editNoteFailures.length === 0 && editReminderFailure === null) {
          onSaved(savedDeal);
          onClose();
          return;
        }

        if (!linesLanded) {
          /*
           * ⚠️ **A REFUSED LINE MUST NOT REACH THE OUTCOME REPORT, and the
           * reason is that report's own Done button: it calls `onSaved`, which
           * every caller reads as "reload and close".** The refused rows are in
           * the grid, each carrying the server's own sentence — closing takes
           * that report away and leaves somebody at the list, told nothing,
           * with their line edits gone from the screen. So this stays on the
           * FORM, where the rows still are.
           *
           * ⚠️ **`setSubmitting(false)` OR THE WHOLE FORM FREEZES.** Every
           * other non-throwing path here ends in `onClose()` or hands over to
           * the outcome report, so a stuck flag has never been reachable
           * before. While `submitting` is true Save renders `loading` (which
           * `Button` makes disabled) and Cancel is disabled too — a form frozen
           * behind a message telling somebody to go and fix a row.
           *
           * A refused note or reminder rides along in the same sentence rather
           * than being dropped: this is the only place either would be said,
           * since the outcome report is exactly what is being skipped.
           */
          setSubmitting(false);
          setFormError(
            [
              headerChanged ? DEAL_SAVED_LINES_NOT : LINES_NOT_SAVED,
              ...editNoteFailures.map(
                (failure) => `The note on ${failure.where} was not recorded. ${failure.message}`,
              ),
              editReminderFailure === null
                ? null
                : `The reminder for ${editReminderFailure.assignee} was not created. ${editReminderFailure.message}`,
            ]
              .filter((sentence): sentence is string => sentence !== null)
              .join(' '),
          );
          return;
        }

        setSubmitting(false);
        setOutcome({
          deal: savedDeal,
          created: false,
          failures: [],
          attached: 0,
          total: 0,
          noteFailures: editNoteFailures,
          reminderFailure: editReminderFailure,
        });
        return;
      }

      /*
       * Create, THEN attach — no create-with-lines endpoint. If the deal
       * itself fails, nothing was attached and ordinary error handling
       * applies; once it exists, the deal is KEPT whatever the lines do.
       * Reporting an overall failure here would send someone looking for a
       * record that is sitting in the list.
       */
      const created = await api.createDeal(payload as DealCreateInput);
      const rows = draftLines;
      const failures = rows.length > 0 ? await attachLines(created.data.id, rows) : [];
      const attached = rows.length - failures.length;
      const saved = attached > 0 ? await rereadDeal(created.data) : created.data;

      /*
       * Third phase, same rule as the second: the deal exists, so a refused
       * note is reported and nothing is undone. It runs after the lines
       * because `saved` is the record the note should name.
       */
      const noteFailures = await writeNotesFor(saved, noteText);
      const reminderFailure = await raiseReminderFor(saved, noteText);

      if (failures.length === 0 && noteFailures.length === 0 && reminderFailure === null) {
        onSaved(saved);
        onClose();
        return;
      }

      setSubmitting(false);
      setOutcome({
        deal: saved,
        created: true,
        failures,
        attached,
        total: rows.length,
        noteFailures,
        reminderFailure,
      });
    } catch (caught) {
      if (caught instanceof ApiError) {
        /*
         * Split against this form's OWN field list, not the shape of the
         * path. `fields` is safe to mark and count; `unroutable` is anything
         * no input here can carry, rendered at form level, never counted as
         * highlighted.
         */
        const { fields, unroutable } = caught.routeFieldErrors(KNOWN_WRITE_KEYS);
        setServerErrors(fields);
        if (unroutable.length > 0) setFormError(unroutable.join(' '));
        else if (Object.keys(fields).length === 0) setFormError(caught.message);
      } else {
        setFormError('Could not reach the server. Check your connection and try again.');
      }
      setSubmitting(false);
    }
  }

  const userOptions = useMemo<ComboboxOption[]>(() => {
    const list = leadStaff.users.map((row) => ({
      value: String(row.id),
      label: row.name,
      hint: row.email,
    }));
    /*
     * The deal's OWN account lead may sit outside the fetched page — or
     * outside the account-lead group, which narrowing makes routine rather
     * than rare. Losing them would read as "follow the company" and the
     * next save would clear a real override nobody touched. `outsideGroupNote`
     * labels a kept non-member; null while the list is unknown so nobody is
     * accused before it arrives.
     *
     * Only the override holder is pinned — a company's lead is deliberately
     * NOT pinned as selectable: this control writes the override, and the
     * way to follow the company is the empty row, not picking the same name.
     */
    const held = deal?.accountLeadIsOverride ? deal.accountLead : null;
    if (held && !list.some((option) => option.value === String(held.id))) {
      const outside = outsideGroupNote(leadStaff);
      list.unshift({
        value: String(held.id),
        label: held.name,
        hint: [held.email, outside].filter(Boolean).join(' · '),
      });
    }
    return list;
  }, [leadStaff, deal]);

  /*
   * The group narrowing survives the helper-text sweep: naming somebody outside
   * `account_lead` is a live 422, so this is the refusal stated up front rather
   * than advice. The failed-fetch and truncation notes were removed with it.
   */
  const staffFootnote = narrowingNote(leadStaff) ?? undefined;

  /*
   * ⚠️ The grid's own row faults are NOT counted here, deliberately — this
   * number drives "N fields need attention, highlighted below", and only
   * this form's own fields are highlighted. `DealLineItemsTable` renders its
   * own banner for blocked rows; counting them here too would state the
   * same problem twice under two different numbers.
   */
  const fieldErrorCount = Object.keys(errors).length;
  /*
   * ⚠️ Save is disabled while the grid withholds rows, on CREATE only.
   * `handleSubmit` refuses too — the disabled button is the optimisation,
   * the refusal is the guarantee.
   */
  const saveDisabled = isEdit
    ? /*
       * ⚠️ **`lineEdits.dirty` IS PART OF "is there anything to save".** This
       * read `!isDirty` alone — the HEADER form — so an edit that touched only
       * line items met a greyed-out "Save changes" beside rows each reading
       * "Unsaved"; and the grid's own Save is gone now, so there is no second
       * control to fall back to. The same defect `QuoteFormModal` closed on
       * 2026-09-01.
       */
      (!isDirty && lineEdits.dirty === 0) || lineEdits.blocked > 0
    : values.dealName.trim() === '' || blockedDraftRows > 0;

  /*
   * ⚠️ No project picker on this form, on the owner's instruction — a
   * project is chosen nowhere while creating a deal; that lives on QUOTE
   * line items. Do not restore the control; see `buildPayload` for why
   * removing it was only half the job.
   *
   * A deal that already HAS a project keeps it, shown read-only below
   * rather than hidden — a blank must never read as an omission. Nothing
   * here can edit or clear it: `projectId` is not a `FormValues` key.
   */
  const existingProjectName = deal?.project?.name ?? null;

  /*
   * The deal WROTE and something after it did not — a line item, a Next task
   * note, or both. The form is gone at this point; what's left is a report of
   * what the several writes did. `onSaved` fires when the report is dismissed,
   * so the list refreshes either way.
   *
   * ⚠️ There is no transaction across the phases and no compensating endpoint
   * safe to fire automatically, so nothing here may imply a rollback. "Nothing
   * was saved" after a successful `POST /deals` is a lie somebody acts on —
   * they close this and create the deal a second time.
   */
  if (outcome !== null) {
    const finish = () => {
      onSaved(outcome.deal);
      onClose();
    };
    const lineTrouble = outcome.failures.length > 0;
    const noteTrouble = outcome.noteFailures.length > 0;
    const verb = outcome.created ? 'created' : 'saved';
    /*
     * Both halves are named when both are true. `lines ?? notes` would drop a
     * refusal the reader has no other way to learn about.
     */
    const refused = [lineTrouble ? 'line items' : null, noteTrouble ? 'notes' : null]
      .filter(Boolean)
      .join(' and ');
    return (
      <Modal
        open
        onClose={finish}
        size="lg"
        dismissOnBackdrop={false}
        title={`${outcome.deal.dealName} was ${verb}`}
        description={`The deal is ${verb}. Some ${refused} were refused.`}
        footer={
          <Button variant="primary" onClick={finish}>
            Done
          </Button>
        }
      >
        <div className="space-y-3">
          {lineTrouble && (
            <>
              {/* ⚠️ "The deal was saved" leads — the load-bearing half. Creating
                  the deal and writing lines are two phases with no transaction
                  across them, so there is nothing to roll back and nothing here
                  may imply there is. */}
              <Notice tone="warning">
                {outcome.attached === 0
                  ? `The deal was saved, but none of its ${outcome.total} line items were added to it.`
                  : `The deal was saved with ${outcome.attached} of ${outcome.total} line items. ${
                      outcome.failures.length === 1 ? 'One was' : `${outcome.failures.length} were`
                    } refused.`}
              </Notice>

              <ul className="space-y-2 rounded-md border border-hairline bg-surface-sunken px-3 py-2">
                {outcome.failures.map((failure) => (
                  <li key={failure.position} className="min-w-0">
                    <p className="text-xs font-medium break-words text-fg">
                      Line {failure.position} of {outcome.total}
                    </p>
                    {/* The server's own words: they name the key, the product or the
                        clash, none of which a paraphrase of ours could carry. */}
                    <p className="text-xs break-words text-fg-muted">{failure.message}</p>
                  </li>
                ))}
              </ul>

              {/* ⚠️ States plainly that refused rows are GONE — they were never
                  written and this form is about to unmount, so nothing holds
                  them; leaving that unsaid invites re-entering assuming the grid
                  still has them. */}
              <p className="text-xs text-fg-muted">
                The refused {outcome.failures.length === 1 ? 'line was' : 'lines were'} not kept —
                add {outcome.failures.length === 1 ? 'it' : 'them'} again from the deal record.
              </p>
            </>
          )}

          {noteTrouble && (
            <>
              {/* ⚠️ The deal — and the Next task ON it — are saved. Only the
                  timeline entry is missing, and saying so is what stops the
                  reader concluding their edit was lost and retyping it. */}
              <Notice tone="warning">
                {`The deal was ${verb}, ${
                  outcome.created ? 'and its' : 'and the'
                } next task is on it. ${
                  outcome.noteFailures.length === 1
                    ? 'One note about it was not recorded.'
                    : `${outcome.noteFailures.length} notes about it were not recorded.`
                }`}
              </Notice>

              <ul className="space-y-2 rounded-md border border-hairline bg-surface-sunken px-3 py-2">
                {outcome.noteFailures.map((failure) => (
                  <li key={failure.where} className="min-w-0">
                    <p className="text-xs font-medium break-words text-fg">
                      Note on {failure.where}
                    </p>
                    {/* The server's own sentence — including the `/notes` 403,
                        which is about this reader's permissions and cannot be
                        paraphrased into something they can act on. */}
                    <p className="text-xs break-words text-fg-muted">{failure.message}</p>
                  </li>
                ))}
              </ul>

              <p className="text-xs text-fg-muted">
                Add {outcome.noteFailures.length === 1 ? 'it' : 'them'} from Tasks &amp; notes if
                the history matters.
              </p>
            </>
          )}

          {/*
            ⚠️ REPORTED SEPARATELY FROM THE NOTES, and not folded into the list
            above, because every row there is captioned "Note on ...". A refused
            TASK shown under that heading would be described as a note that was
            not recorded -- a confidently wrong sentence about the one thing on
            this screen already going wrong.

            It renders on its own condition too: a reminder can be refused on a
            save where every note landed, and `noteTrouble` would hide it.
          */}
          {outcome.reminderFailure !== null && (
            <Notice tone="warning">
              {`The deal was ${verb} and its next task is on it, but the reminder for ${outcome.reminderFailure.assignee} was not created. ${outcome.reminderFailure.message} Add it from Tasks & notes if somebody needs prompting.`}
            </Notice>
          )}
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open
      onClose={onClose}
      /*
       * ⚠️ `xl` because this form contains a TABLE — the only reason to use
       * `xl`, never to widen a column of fields. Below `sm` the panel is
       * `w-full`, so this stays a full-bleed sheet on a phone. Header fields
       * must still collapse to ONE column at 320px.
       */
      size="xl"
      dismissOnBackdrop={false}
      initialFocusRef={nameRef}
      title={deal ? `Edit ${deal.dealName}` : 'New deal'}
      /*
       * 🔴 **THE HEADER, NOT THE FOOTER — owner's decision, 2026-09-14.** Cancel
       * and Save moved up beside the title on all three document forms, because
       * this one is a screen tall and carries a line grid with a Save of its
       * own partway down it. **The pair moved together**: splitting them across
       * the two ends of a scrolling dialog leaves a reader hunting the control
       * that was next to the one they just found.
       *
       * ⚠️ **The footer was NOT scrolling away and this does not fix a
       * reachability bug** — the panel is a flex column whose body scrolls, so
       * both ends were always pinned. It is about where the eye already is.
       */
      headerActions={
        <>
          {/*
            ⚠️ **The one thing a disabled Save cannot say for itself.** The grid
            renders its own banner over the offending rows, but that banner says
            nothing about this button — and with the grid's own Save gone, a
            greyed-out control with no reason beside it is the whole of what a
            person sees.
          */}
          {isEdit && lineEdits.blocked > 0 && (
            <p className="self-center text-xs break-words text-danger sm:mr-auto">
              {lineEdits.blocked === 1
                ? 'One line item below cannot be saved yet — fix or remove it.'
                : `${lineEdits.blocked} line items below cannot be saved yet — fix or remove them.`}
            </p>
          )}
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            form={formId}
            variant="primary"
            loading={submitting}
            disabled={saveDisabled}
          >
            {isEdit ? 'Save changes' : 'Create deal'}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit} noValidate className="space-y-5">
        <FormErrorSummary formError={formError} fieldErrorCount={fieldErrorCount} />

        <div className="grid gap-3 sm:grid-cols-2">
          <div
            className="sm:col-span-2"
            ref={(node) => {
              nameRef.current = node?.querySelector('input') ?? null;
            }}
          >
            <Field label="Deal name" required error={errors.dealName}>
              {({ id, 'aria-describedby': describedBy }) => (
                <Input
                  id={id}
                  value={values.dealName}
                  maxLength={MAX_LENGTHS.dealName}
                  invalid={Boolean(errors.dealName)}
                  aria-describedby={describedBy}
                  onChange={(event) => {
                    /*
                     * The ONE writer of `nameTouched` — the whole guard on
                     * the default name above. Set even when emptied:
                     * deleting a suggested name is a decision, and
                     * re-seeding on the next company change would put back
                     * exactly what was removed.
                     */
                    nameTouched.current = true;
                    setField('dealName', event.target.value);
                  }}
                />
              )}
            </Field>
          </div>

          {/*
            * ⚠️ The Company picker below marks itself `required` on CREATE
            * only, matching the rule `validate` applies: a new deal has to name
            * an APPROVED company, an existing one is left exactly as it was.
            * The locked branch takes no marker — its value is already there and
            * cannot be changed, so an asterisk would point at nothing the
            * reader can act on. It is still refused on save if that company
            * turns out not to be approved, which is the only check that can
            * reach a company arriving with no picker.
            */}
          {lockedCompany ? (
            <Field label="Company" error={errors.companyId}>
              {({ id, 'aria-describedby': describedBy }) => (
                <Input
                  id={id}
                  value={lockedCompany.name}
                  readOnly
                  aria-readonly="true"
                  className="bg-surface-sunken text-fg-secondary"
                  invalid={Boolean(errors.companyId)}
                  aria-describedby={describedBy}
                />
              )}
            </Field>
          ) : (
            <Field label="Company" required={!isEdit} error={errors.companyId}>
              {({ id, 'aria-describedby': describedBy }) => (
                <Combobox
                  id={id}
                  value={values.companyId}
                  // Not `setField` — a company change clears the contact with
                  // it, and the two have to be one state update. See
                  // `onCompanyChange`.
                  onChange={onCompanyChange}
                  options={companies.options}
                  onSearch={companies.setTerm}
                  loading={companies.loading}
                  emptyOptionLabel="Unassigned"
                  searchPlaceholder="Search companies…"
                  footnote={companies.footnote}
                  invalid={Boolean(errors.companyId)}
                  aria-describedby={describedBy}
                />
              )}
            </Field>
          )}

          <ContactPickerField
            value={values.contactId}
            onChange={onContactChange}
            options={contactOptions}
            onSearch={contacts.setTerm}
            loading={contacts.loading}
            footnote={contacts.footnote}
            error={errors.contactId}
            scope={contactScope}
            company={contactCompany}
          />

          {/*
            * Project is READ-ONLY here, only when the record already has
            * one — no picker on create or edit; the project is chosen on
            * quote line items instead. Shown rather than hidden because a
            * value the record holds must not read as an omission. No
            * `error` slot: this form never sends `projectId`.
            */}
          {existingProjectName !== null && (
            <div className="sm:col-span-2">
              <Field label="Project">
                {({ id, 'aria-describedby': describedBy }) => (
                  <Input
                    id={id}
                    value={existingProjectName}
                    readOnly
                    aria-readonly="true"
                    className="bg-surface-sunken text-fg-secondary"
                    aria-describedby={describedBy}
                  />
                )}
              </Field>
            </div>
          )}

          <PickerField
            kind="stage"
            label="Stage"
            value={values.stageId}
            onChange={onStageChange}
            options={pickers.stages}
            loading={pickers.loading}
            failed={pickers.failedBy.stages}
            stored={deal?.stage ?? null}
            error={errors.stageId}
            onCreated={(row) => pickers.addStage(row as DealStage)}
          />

          {movingToLost && (
            <div className="min-w-0 sm:col-span-2">
              <LostReasonFields
                reasonId={lostReasonId}
                note={lostNote}
                onReasonChange={(next) => {
                  setLostReasonId(next);
                  setClientErrors((prev) =>
                    'lostReasonId' in prev ? without(prev, 'lostReasonId') : prev,
                  );
                  setServerErrors((prev) =>
                    'lostReasonId' in prev ? without(prev, 'lostReasonId') : prev,
                  );
                }}
                onNoteChange={setLostNote}
                reasonError={errors.lostReasonId}
                noteError={errors.lostNote}
                disabled={submitting}
              />
            </div>
          )}

          {/*
            * Account lead — where Owner used to be, and not the same kind
            * of control.
            *
            * ⚠️ Only once a company is chosen: with no company there is
            * nothing to follow or override, so the picker could name
            * neither outcome. Appears the moment a company is selected
            * (including a locked one) and on edit.
            *
            * The one exception is an existing deal holding an override with
            * no company — shown because a real value must never read as an
            * omission, and it says plainly there is nothing to fall back to.
            *
            * The value is the OVERRIDE. Empty means "follow the company",
            * never "nobody".
            */}
          {showAccountLead && (
            <Field label="Account lead" error={errors.accountLeadId}>
              {({ id, 'aria-describedby': describedBy }) => (
                <Combobox
                  id={id}
                  value={values.accountLeadId}
                  onChange={(next) => setField('accountLeadId', next)}
                  options={userOptions}
                  loading={usersLoading}
                  /* Both say the same thing: the trigger reads as the state it
                     is in even before the list is opened. */
                  emptyOptionLabel={followLabel}
                  placeholder={followLabel}
                  searchPlaceholder="Search staff…"
                  footnote={staffFootnote}
                  invalid={Boolean(errors.accountLeadId)}
                  aria-describedby={describedBy}
                />
              )}
            </Field>
          )}

          {/*
            * ⚠️ **Deal type and Lead source used to sit here and are GONE on the
            * owner's decision — neither is touched from this modal at all.**
            * Written down rather than deleted silently, because a missing field
            * reads as an oversight and the obvious "restore the picker" fix is
            * only half the job: see `ID_KEYS`, where their absence is what stops
            * an edit emitting `dealTypeId: null` and clearing a stored value.
            *
            * A deal that already holds either keeps it — nothing here can read,
            * write or clear them. Both are still editable on the deal detail
            * page through `InlineDealFields`, which is a separate surface.
            */}

          <Field label="Amount" error={errors.amount}>
            {({ id, 'aria-describedby': describedBy }) => (
              <Input
                id={id}
                // Text with a numeric mode, not type="number": the value is an
                // exact decimal string end to end and a number input hands back
                // a rounded or locale-formatted one.
                inputMode="decimal"
                value={values.amount}
                invalid={Boolean(errors.amount)}
                aria-describedby={describedBy}
                onChange={(event) => setField('amount', event.target.value)}
              />
            )}
          </Field>

          {/*
            * ⚠️ A PICKER off the currency master, not the free-text box that
            * stood here. The box was never fed by anything: this modal did not
            * read `/currencies` at all, so somebody typing `GPB` learned about
            * it from a 422 on save. `QuoteFormModal` has always used this hook
            * and this option builder; the two now offer the same set, the same
            * order and the same VALUE.
            */}
          <Field label="Currency" error={errors.currency}>
            {({ id, 'aria-describedby': describedBy }) => (
              <Combobox
                id={id}
                /*
                 * The three-letter CODE, never a currency id. `d_deals.currency`
                 * is a CHAR(3) with no foreign key into the master, so an id
                 * would be refused or — worse — coerced into a nonsense code.
                 */
                value={values.currency}
                onChange={onCurrencyChange}
                options={currencyChoices}
                loading={currenciesLoading}
                emptyOptionLabel="Not set"
                searchPlaceholder="Search currencies…"
                invalid={Boolean(errors.currency)}
                aria-describedby={describedBy}
              />
            )}
          </Field>

          <Field label="Closing date" error={errors.closingDate}>
            {({ id, 'aria-describedby': describedBy }) => (
              <Input
                id={id}
                // A native date input emits YYYY-MM-DD from local parts, which
                // is the wire format — no Date, no timezone drift.
                type="date"
                value={values.closingDate}
                invalid={Boolean(errors.closingDate)}
                aria-describedby={describedBy}
                onChange={(event) => setField('closingDate', event.target.value)}
              />
            )}
          </Field>

          <div className="sm:col-span-2">
            {/*
              ⚠️ LABEL ONLY. The owner renamed "Next step" to "Next task"
              (2026-08-31); the column is still `d_deals.next_step` and the wire
              key is still `nextStep`, so `FormValues`, `TEXT_KEYS`,
              `MAX_LENGTHS` and `KNOWN_WRITE_KEYS` are all untouched. Renaming
              the key here would send a name the `.strict()` schema does not
              know — a 422 whose `path` is `''`, which routes to no control.
            */}
            <Field label="Next task" error={errors.nextStep}>
              {({ id, 'aria-describedby': describedBy }) => (
                <Input
                  id={id}
                  value={values.nextStep}
                  maxLength={MAX_LENGTHS.nextStep}
                  invalid={Boolean(errors.nextStep)}
                  aria-describedby={describedBy}
                  onChange={(event) => setField('nextStep', event.target.value)}
                />
              )}
            </Field>

            {/*
              ── Remind on / Assign to ──────────────────────────────────────
              Owner's request, 2026-09-10. Both optional: the text alone still
              writes the note it always did and raises nothing.

              ⚠️ These do NOT reach `d_deals`. They create a task attached to
              the deal, which is what puts it in somebody's My Tasks, in their
              day brief and in the bell that raises a toast when it falls due.
            */}
            <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Remind on">
                {({ id, 'aria-describedby': describedBy }) => (
                  <Input
                    id={id}
                    // Native date input: emits YYYY-MM-DD from local parts,
                    // which is the wire format. No Date, no timezone drift.
                    type="date"
                    value={taskDueDate}
                    aria-describedby={describedBy}
                    onChange={(event) => setTaskDueDate(event.target.value)}
                  />
                )}
              </Field>

              <Field label="Assign to">
                {({ id, 'aria-describedby': describedBy }) => (
                  <Combobox
                    id={id}
                    value={taskAssigneeId}
                    onChange={setTaskAssigneeId}
                    options={assigneeOptions}
                    loading={usersLoading}
                    /*
                     * Empty is not "nobody" -- the server defaults an omitted
                     * assignee to the caller, which is the whole reason a date
                     * with no person chosen means "remind me". Sending an
                     * explicit null would mean UNASSIGNED and is a 422 whenever
                     * a due date is present.
                     */
                    emptyOptionLabel="Me"
                    searchPlaceholder="Search people…"
                    aria-describedby={describedBy}
                  />
                )}
              </Field>
            </div>

            {/*
              ⚠️ SAYS WHAT IT CANNOT DO. There is no push and no service worker
              here, so a reminder cannot reach somebody with EMERGE closed --
              and a sentence implying otherwise is a promise the system does not
              keep. The wording is deliberately about where it appears rather
              than about being "notified".
            */}
            <p className="mt-2 text-xs text-fg-muted">
              {values.nextStep.trim() === ''
                ? 'Type a next task to set a date or hand it to someone.'
                : taskDueDate === '' && taskAssigneeId === ''
                  ? 'Recorded on the deal. Add a date or a person to put it in a task list.'
                  : `It lands in ${
                      taskAssigneeId === '' ? 'your' : 'their'
                    } My Tasks straight away${
                      taskDueDate === ''
                        ? '. Add a date if it should raise a reminder.'
                        : ', and reminds at 9am on that date while EMERGE is open.'
                    }`}
            </p>
          </div>
        </div>

        {/*
          * ── LINE ITEMS ─────────────────────────────────────────────────
          *
          * ⚠️ Replaced a short product+quantity section on a reversed owner
          * decision — a deal now carries a full basket, for information,
          * that converts onward to a quote or invoice. If the reversal
          * turns out not to be wanted, the fix is reverting to the whole
          * old section, not partially narrowing this grid.
          *
          * ⚠️ ONE component, two modes, never two editors — the same grid
          * on create and edit, as `QuoteFormModal`/`QuoteDetailPage` do, so
          * the two paths cannot word or validate a line differently.
          *
          * `DealDetailPage` still mounts `LineItemFormModal` for its own
          * per-line editing and is deliberately untouched here.
          */}
        <div className="min-w-0 border-t border-border pt-4">
          {isEdit && deal ? (
            /*
             * EDIT — the deal exists, so the grid addresses
             * `/deals/{id}/products` directly and owns its own writes.
             */
            <DealLineItemsTable
              dealId={deal.id}
              /*
               * The HEADER project's set — not a line's option list, since
               * each row draws cities from its own project. Kept only for
               * naming a city we hold no other record of.
               */
              cities={deal.cities}
              currency={deal.currency}
              canEdit={ability.canUpdate('deals')}
              canDelete={ability.canDelete('deals')}
              {...(denialReason(ability, 'delete', 'deals')
                ? { deleteDenialReason: denialReason(ability, 'delete', 'deals') as string }
                : {})}
              /*
               * ⚠️ **Passing `registerSave` REMOVES the grid's own Save
               * button**, so this form's footer is the only way these rows
               * reach the server. `handleSubmit` MUST call it — see the line
               * save there. Owner's instruction, 10 September 2026: one button.
               */
              onUnsavedChange={setLineEdits}
              registerSave={registerLineSave}
              /*
               * ⚠️ `onLinesChanged` is deliberately NOT passed. A deal line
               * write changes nothing on the deal record — no header
               * totals, and `deal.project`/`deal.cities` don't derive from
               * lines — so nothing this modal shows goes stale.
               *
               * ⚠️ And the obvious implementation is actively WRONG: every
               * caller closes this modal from inside `onSaved`, so an
               * `onSaved(freshDeal)` callback here would tear the modal
               * down after every single line save, discarding the rows
               * still being worked on. The parent re-reads when the form
               * closes.
               */
            />
          ) : (
            /*
             * CREATE — the grid holds the rows and issues NO request.
             * `POST /deals/{id}/products` needs an id and there is no bulk
             * endpoint, so nothing can be written until the deal exists.
             * Draft mode edits rows exactly as on a saved deal and hands up
             * the very payloads the POST takes.
             */
            <DealLineItemsTable
              mode="draft"
              /*
               * ⚠️ Empty, structurally: a deal created here has NO header
               * project, since `projectId` isn't a `FormValues` key.
               * Inventing a city set from `/cities` would offer rows every
               * line write refuses.
               */
              cities={NO_HEADER_CITIES}
              /*
               * The TYPED currency, not a saved one — there is no saved one
               * yet. Uppercased, since that is what will be stored.
               */
              currency={values.currency.trim() === '' ? null : values.currency.trim().toUpperCase()}
              canEdit={ability.canCreate('deals')}
              /*
               * ⚠️ `true`, and NOT `ability.canDelete('deals')` — on a saved
               * deal that's an admin-tier DELETE, but a draft row has never
               * been sent anywhere, so removing it issues no request.
               * Gating it on delete ability would trap a `basic_user` with a
               * row they added by mistake.
               */
              canDelete
              /*
               * The seeded basket — owner's instruction, 2026-09-13, so the "+"
               * on a company row opens this form already carrying rows built
               * from that company's projects of interest.
               *
               * ⚠️ **CREATE-ONLY BY CONSTRUCTION, not by a flag.** This branch
               * renders only while `deal` is undefined, so there is no edit
               * path for a seed to reach — the same guarantee the quote form
               * relies on rather than a `mode` check somebody has to remember.
               * A stored deal's rows are the record's own and the edit-branch
               * grid owns its own writes.
               *
               * ⚠️ **A SEED IS NOT A SEND.** These rows are visible and
               * editable before anything exists, they go out through the same
               * `draftLines` → `attachLines` path a hand-typed row does, and a
               * seeded row the grid cannot state still counts towards
               * `blockedDraftRows` and still disables Save. Nothing here makes
               * the create atomic and nothing here should try to.
               */
              initialDraftRows={prefill?.lines}
              onDraftLinesChange={setDraftLines}
              onDraftBlockedChange={setBlockedDraftRows}
              contextNote={
                /*
                 * ⚠️ Kept short: the one place the create's NON-ATOMICITY is
                 * stated before it can bite. Never reword into a promise
                 * that nothing will be saved unless everything is.
                 */
                <Notice tone="neutral">
                  Saved with the deal, one line at a time. Nothing is undone on your behalf.
                </Notice>
              }
            />
          )}
        </div>

        {/*
          * ⚠️ The "Xero" fieldset (a Quote ID box over `xeroQuoteId`) and the
          * Description textarea BOTH stood here and were removed on the
          * owner's decision, 2026-08-31. See `TEXT_KEYS` for why removing
          * each CONTROL also meant removing its `FormValues` key — and for
          * the fact that `description` still holds real data on live deals
          * and is still read back; only the write and the control went.
          */}
      </form>
    </Modal>
  );
}

/**
 * Who the SELECTED company says leads it.
 *
 * The account-lead picker's empty row must be able to NAME the person a
 * clear would fall back to, and nothing on this form carries that — so the
 * company is read once per distinct selection.
 *
 * `seed` short-circuits the request for the common edit case: the selection
 * is still the record's own company, which already serialises
 * `companyAccountLead`, saving a request off the shared limiter.
 *
 * A failure is NOT an error state and never claims the company names
 * nobody: `known` stays false, and every label falls back to promising only
 * "follow the company".
 */
function useCompanyAccountLead(
  companyId: string,
  seed: { companyId: string; lead: UserSummary | null } | null,
): CompanyLead {
  const seeded = companyId !== '' && seed !== null && seed.companyId === companyId;
  const [loaded, setLoaded] = useState<{
    companyId: string;
    lead: UserSummary | null;
    naming: { name: string; shortName: string | null };
    primaryContact: { id: number; name: string } | null;
    currency: string | null;
    approvalStatus: CompanyApprovalStatus;
  } | null>(null);

  useEffect(() => {
    if (companyId === '' || seeded) return;
    const controller = new AbortController();
    api
      .getCompany(Number(companyId), controller.signal)
      .then((envelope) => {
        if (controller.signal.aborted) return;
        setLoaded({
          companyId,
          lead: envelope.data.accountLead,
          // Captured from the same read, so the default name and the account
          // lead can never describe two different companies.
          naming: { name: envelope.data.name, shortName: envelope.data.shortName },
          // Third rider on the same read, for the same reason — see the field
          // note on `CompanyLead.primaryContact`.
          primaryContact: primaryContactSeed(envelope.data),
          /*
           * Fourth rider. Normalised HERE rather than at the seed, so exactly
           * one place decides what "this company names a usable currency"
           * means: trimmed, uppercased and checked against `CURRENCY_PATTERN`,
           * with everything else — absent, blank, or a code in some other shape
           * — collapsing to `null`, which seeds nothing.
           *
           * ⚠️ `currency.code`, never `currency.id`. The id is nullable
           * precisely because the legal set is Xero's rather than our master's,
           * and the column this feeds stores the code anyway.
           */
          // Falls back to the billing region's currency, as the invoice form
          // does: a company awaiting approval has no currency of its own yet
          // (the approver sets it), and a deal is the one document it can have.
          currency:
            usableCurrencyCode(envelope.data.currency?.code ?? null) ??
            usableCurrencyCode(envelope.data.billingRegionDefaults?.currencyCode ?? null),
          /*
           * Fifth rider, and the one the create-time refusal turns on. Taken
           * from this read rather than from the picker option because the
           * `lockedCompany` route renders no picker — and that is precisely the
           * route somebody takes from a company they have just raised, which is
           * the only kind that is not approved yet.
           *
           * ⚠️ Carried RAW, never folded to a boolean. `pending` and `rejected`
           * are different refusals with different next steps, and a
           * `=== 'approved'` here would leave the message unable to say which.
           */
          approvalStatus: envelope.data.approvalStatus,
        });
      })
      .catch(() => {
        // Deliberately nothing. `known` stays false and the labels say only
        // what they can, rather than reporting an absence that was never read.
      });
    return () => controller.abort();
  }, [companyId, seeded]);

  if (companyId === '') {
    return {
      known: false,
      lead: null,
      naming: null,
      primaryContact: null,
      currency: null,
      approvalStatus: null,
    };
  }
  /*
   * The edit-mode short-circuit knows the lead and nothing else. `naming` stays
   * null, which is right in both directions: it was not read, and an edit never
   * re-derives a name anyway.
   *
   * ⚠️ `primaryContact` stays null here for the same reason and it costs
   * nothing: this branch is reached only while the selection is still the
   * record's OWN company, where the stored contact is the document's own and
   * the seed is locked out by `contactTouched` regardless. The moment somebody
   * moves the company, `seeded` goes false and the read below runs.
   */
  /*
   * ⚠️ `currency` stays null on this branch for the same reason `naming` and
   * `primaryContact` do, and it costs nothing: the short-circuit is only
   * reached while the selection is still the record's OWN company, where the
   * stored currency is the deal's own and `currencyTouched` — true on every
   * edit — locks the seed out regardless. Move the company and `seeded` goes
   * false, so the read below runs.
   */
  /*
   * ⚠️ `approvalStatus` stays null on this branch, and it costs nothing for a
   * structural reason rather than a lucky one: `seed` is built from
   * `deal.company`, so this short-circuit is reachable only on an EDIT — and
   * the refusal is create-only. A create has no `deal`, so `seeded` is false
   * and the read below always runs before there is anything to refuse.
   */
  if (seeded && seed) {
    return {
      known: true,
      lead: seed.lead,
      naming: null,
      primaryContact: null,
      currency: null,
      approvalStatus: null,
    };
  }
  // Pinned to the id it was fetched for, so a stale answer is discarded during
  // render instead of naming the previous company's lead for a frame.
  if (loaded !== null && loaded.companyId === companyId) {
    return {
      known: true,
      lead: loaded.lead,
      naming: loaded.naming,
      primaryContact: loaded.primaryContact,
      currency: loaded.currency,
      approvalStatus: loaded.approvalStatus,
    };
  }
  /*
   * Fetch in flight, or it FAILED — the two are folded together here and the
   * fold is deliberate. `newDealCompanyError` stands down on `null` either way,
   * so a save made in the window before the read lands, or after it failed, is
   * allowed through rather than refused on a fact nobody has. That is a real,
   * accepted hole in a client-side rule the server does not enforce at all: the
   * alternative is refusing a company that is very probably approved, which is
   * a false statement the person has no way to clear.
   */
  return {
    known: false,
    lead: null,
    naming: null,
    primaryContact: null,
    currency: null,
    approvalStatus: null,
  };
}

/**
 * A company's default currency, as a code this form may actually seed — or
 * `null`, which seeds nothing.
 *
 * ⚠️ The pattern check is not decoration. `d_deals.currency` is refused unless
 * it is exactly three capital letters and the server folds no case, so a
 * company holding a code in any other shape would be seeded straight into a
 * guaranteed 422 on a field the person never touched. Dropping it leaves the
 * picker empty, which is honest and fixable.
 */
function usableCurrencyCode(code: string | null): string | null {
  if (code === null) return null;
  const upper = code.trim().toUpperCase();
  return upper !== '' && CURRENCY_PATTERN.test(upper) ? upper : null;
}

/**
 * Server-side search for the company and contact pickers.
 *
 * Both directories are far larger than one page, so filtering a fetched page
 * locally would only ever search the first 100 names. `Combobox` debounces
 * the term itself.
 *
 * The record's existing link is pinned into the list: a search that doesn't
 * happen to return it must not make the field look unassigned.
 *
 * ⚠️ **The contacts branch is SCOPED to one company** (`scopeCompanyId`), which
 * is a different thing from the `enabled` flag below it and behaves differently
 * in the one case that matters: a disabled picker keeps whatever it last read,
 * while an unscoped one is EMPTY on purpose. See `src/lib/contact-scope.ts` for
 * the whole rule, the wording, and the measurement that the API does not check
 * the pair itself.
 */
function useLookupSearch(
  resource: 'companies' | 'contacts',
  pinned: { id: number; name: string } | null,
  enabled = true,
  /**
   * The company the contact list is scoped to. `undefined` on the companies
   * picker; `null` means "this IS a scoped picker and no company is chosen",
   * which fetches nothing and offers nothing — see `CONTACT_NEEDS_COMPANY`.
   */
  scopeCompanyId?: number | null,
  /**
   * Companies picker, CREATE only: drop companies that are not approved and
   * report how many were dropped.
   *
   * ⚠️ Off on EDIT, deliberately. The rule the owner gave is about raising a
   * NEW deal; narrowing an edit's picker would be this app restricting a write
   * the server accepts, on a field whose refusal has never been measured — and
   * it would strand a deal somebody is trying to move. The pin below is
   * unconditional in either case, so a deal already holding a company that has
   * since been rejected still renders it and stays editable.
   */
  withholdUnapproved = false,
) {
  const [term, setTerm] = useState('');
  /*
   * Rows and the scope they ANSWER FOR, in one state, so "which company is this
   * list for" and "which company is selected" can never disagree for a frame —
   * the frame in which somebody could otherwise pick the previous company's
   * contact. Derived rather than cleared in a second effect, exactly as
   * `ProjectFormModal` tags its intakes with their cycle.
   *
   * ⚠️ That frame is worth closing here even though the server would accept the
   * write: it accepts it SILENTLY, so there is no 422 to fall back on.
   */
  const [result, setResult] = useState<{
    scope: number | null | 'unscoped';
    rows: { id: number; name: string }[];
    truncated: boolean;
    failed: boolean;
    /**
     * How many rows this page returned and `withholdUnapproved` removed. `0`
     * whenever the filter is off, so it is a count of what was WITHHELD and
     * never a count of what is unapproved in the directory.
     */
    withheld: number;
  } | null>(null);
  const [loadedTerm, setLoadedTerm] = useState<string | null>(null);

  const scope: number | null | 'unscoped' =
    resource === 'contacts' ? scopeCompanyId ?? null : 'unscoped';

  useEffect(() => {
    // A locked field renders no picker, so there is nothing for a page of
    // matches to fill: the pinned row alone is the whole option list.
    if (!enabled) return;

    // No company, nothing to fetch. The empty answer is DERIVED below rather
    // than written here: a `setState` in this branch would be a render's worth
    // of cascade to reach a value already known during render.
    if (scope === null) return;

    const controller = new AbortController();
    const params = { search: term, perPage: 100, sort: 'name', order: 'asc' } as const;
    /*
     * Each branch normalises its OWN response, because only the companies one
     * has an `approvalStatus` to read. Narrowing after the join instead would
     * mean testing a key that exists on one of the two row types, which is the
     * shape `tsc` cannot help with once both are mapped to `{id, name}`.
     */
    const request: Promise<{
      rows: { id: number; name: string }[];
      truncated: boolean;
      withheld: number;
    }> =
      resource === 'companies'
        ? api.listCompanies(params, controller.signal).then((response) => {
            /*
             * ⚠️ The filter is the OPTIMISATION, not the guarantee — a company
             * can be approved or rejected between this page landing and Save.
             * `newDealCompanyError` is the backstop, and it reads the company
             * itself rather than this list.
             */
            const offered = withholdUnapproved
              ? response.data.filter((row) => row.approvalStatus === 'approved')
              : response.data;
            return {
              rows: offered.map((row) => ({ id: row.id, name: row.name })),
              truncated: response.meta.totalPages > 1,
              withheld: response.data.length - offered.length,
            };
          })
        : // ⚠️ A SINGLE id — `?companyId=15,16` is a 422 on this endpoint.
          api
            .listContacts({ ...params, companyId: scope as number }, controller.signal)
            .then((response) => ({
              rows: response.data.map((row) => ({ id: row.id, name: row.name })),
              truncated: response.meta.totalPages > 1,
              withheld: 0,
            }));

    request
      .then((answer) => {
        if (controller.signal.aborted) return;
        setResult({ scope, ...answer, failed: false });
      })
      .catch(() => {
        /*
         * ⚠️ `failed`, not merely empty. Both render no options, and telling
         * somebody a company has no contacts when the request simply did not
         * come back sends them to create a person who already exists.
         *
         * ⚠️ `withheld: 0` here is the same discipline: nothing was withheld,
         * because nothing was read. A failed fetch must not report a count that
         * would read as "some companies are awaiting approval".
         */
        if (!controller.signal.aborted) {
          setResult({ scope, rows: [], truncated: false, failed: true, withheld: 0 });
        }
      })
      .finally(() => {
        // Records which term the list now answers, so `loading` is derived
        // during render rather than toggled from inside the effect.
        if (!controller.signal.aborted) setLoadedTerm(term);
      });

    return () => controller.abort();
  }, [resource, term, enabled, scope, withholdUnapproved]);

  /**
   * The result, but only while it describes the scope currently selected — the
   * previous company's rows are not a stale version of this company's list, they
   * are the wrong list, so they are dropped rather than shown dimmed.
   *
   * ⚠️ A STABLE reference in every branch, because `options` memoises on it. The
   * no-company answer is the module-level `NO_SCOPE_RESULT` rather than a fresh
   * `{rows: []}`, and an inline literal here would rebuild the option list on
   * every render — which is the memo silently doing nothing.
   */
  const answered =
    scope === null
      ? NO_SCOPE_RESULT
      : result !== null && result.scope === scope
        ? result
        : null;
  // With no company there is nothing in flight and nothing to wait for, so the
  // field says why it is empty instead of spinning at a request never coming.
  const loading = enabled && scope !== null && (loadedTerm !== term || answered === null);

  /**
   * True when the pinned value is NOT among the rows the list returned — i.e.
   * the option exists only because it was pinned back in.
   *
   * The call site turns this into the "not one of this company's contacts"
   * label. Computed here rather than there because only this hook can tell an
   * injected option from a fetched one once they are all `ComboboxOption`s.
   */
  const pinInjected =
    pinned !== null && !(answered?.rows ?? []).some((row) => row.id === pinned.id);

  const options = useMemo<ComboboxOption[]>(() => {
    const list: ComboboxOption[] = (answered?.rows ?? []).map((row) => ({
      value: String(row.id),
      label: row.name,
    }));
    /*
     * ⚠️ The pin is UNCONDITIONAL; only the "not on this company" REASON is
     * gated (at the call site, on `contactScopeIsAuthoritative`). A stored
     * contact dropped from the list renders as "Unassigned" over a record that
     * holds one, and the next save writes that emptiness back.
     */
    if (pinned && !list.some((option) => option.value === String(pinned.id))) {
      list.unshift({ value: String(pinned.id), label: pinned.name });
    }
    return list;
  }, [answered, pinned]);

  /*
   * ⚠️ Truncation and withholding can BOTH be true, so both are said. A
   * `truncated ? … : withheld ? …` between them would drop the withheld count
   * on exactly the busy list where somebody is most likely to be hunting a
   * company that is missing for the other reason — the silent-`??` shape this
   * codebase records four instances of.
   */
  /*
   * The withheld-company note survives the 2026-09-05 helper-text sweep: a
   * deal may only name an APPROVED company, so this states a live refusal
   * rather than advice. The truncation half beside it was removed.
   */
  const footnote = withheldCompanyNote(answered?.withheld ?? 0);

  return {
    options,
    pinInjected,
    setTerm,
    loading,
    footnote,
    /** What the Contact field needs to word its own hint. */
    scope: {
      companyId: typeof scope === 'number' ? scope : null,
      loading,
      failed: answered?.failed ?? false,
      count: answered?.rows.length ?? 0,
      searching: term.trim() !== '',
    },
  };
}

/**
 * The answer for a scoped picker with no company chosen: no rows, not loading,
 * not failed.
 *
 * ⚠️ Module-level so the reference is STABLE. `useLookupSearch` memoises its
 * option list on this object's identity, and a literal rebuilt each render
 * would make the memo a no-op while still looking like one.
 */
const NO_SCOPE_RESULT = {
  scope: null as number | null | 'unscoped',
  rows: [] as { id: number; name: string }[],
  truncated: false,
  failed: false,
  withheld: 0,
};
